// @ts-nocheck
// =============================================================================
//  KitchenControl — HACCP-module
//  Gebruik vanuit de schil: <HaccpModule sb={...} tenantId={...} activeTab={...} onTabChange={...} />
//
//  ✅ FIX: eigen tabbar verwijderd — navigatie loopt via de schil (subItems in zijbalk)
//          activeTab en onTabChange komen binnen als props van App.tsx
// =============================================================================
import { useState, useEffect, useRef } from "react";
import { parseCoolingTemperature, coolingStatus, coolingMinutes, coolingDeviation } from "./coolingLogic.mjs";
import { useBeheerPin } from "./ui/BeheerPin";

// Migreer medewerkers van haccp_config naar Team als Team nog leeg is
function migreerMedewerkers(haccpMeds) {
  if (!haccpMeds?.length) return;
  try {
    const teamData = JSON.parse(localStorage.getItem("kc-team-v1") || "{}");
    if (teamData?.medewerkers?.length) return; // Team heeft al data
    const uid = () => Date.now() + "-" + Math.floor(Math.random() * 9999);
    const nieuw = haccpMeds.map(m => ({ id: m.id || uid(), naam: m.naam }));
    localStorage.setItem("kc-team-v1", JSON.stringify({ medewerkers: nieuw }));
    console.log("[HACCP] Migratie: " + nieuw.length + " medewerkers overgezet naar Team");
  } catch {}
}

let _sb = null;
let _tid = null;
let _rt = null; // Supabase-js client voor Realtime (WebSocket)
function setBackend(sb, tenantId, rt) {
  _sb = sb;
  _tid = tenantId;
  if (rt !== undefined) _rt = rt;
}
const tf = () => (_tid ? `tenant_id=eq.${_tid}&` : "");

async function sbGet(table, params = "") {
  try {
    if (!_sb) return [];
    const d = await _sb.select(table, `${tf()}${params}&order=created_at.desc`);
    return Array.isArray(d) ? d : [];
  } catch {
    return [];
  }
}
async function sbPost(table, body) {
  try {
    if (!_sb) return null;
    const d = await _sb.insert(table, {
      ...(_tid ? { tenant_id: _tid } : {}),
      ...body,
    });
    return Array.isArray(d) ? d[0] : d;
  } catch {
    return null;
  }
}
async function sbPatch(table, id, body) {
  try {
    if (_sb) await _sb.update(table, `id=eq.${id}`, body);
  } catch {}
}
async function sbDelete(table, id) {
  try {
    if (_sb) await _sb.remove(table, `id=eq.${id}`);
  } catch {}
}
async function sbLoadConfig() {
  try {
    if (!_sb) return null;
    const d = await _sb.select(
      "haccp_config",
      `${tf()}select=instellingen&limit=1`
    );
    return d?.[0]?.instellingen || null;
  } catch (e) {
    console.error("HACCP: config laden mislukt —", e);
    return null;
  }
}
// Is dit een "lege" (onbewerkte standaard) config? Gebruikt door het veiligheidsslot.
const cfgLeeg = (c) =>
  !(c?.koelunits?.length) &&
  !(c?.medewerkers?.length) &&
  !(c?.leveranciers?.length) &&
  !(c?.thermometers?.length) &&
  !(c?.allergenenGerechten?.length) &&
  !(c?.schoonmaakTaken?.dagelijks?.length) &&
  !(c?.schoonmaakTaken?.wekelijks?.length) &&
  !(c?.schoonmaakTaken?.vierweeks?.length);

async function sbSaveConfig(data) {
  try {
    if (!_sb || !_tid) return false;
    const existing = await _sb.select(
      "haccp_config",
      `${tf()}select=instellingen&limit=1`
    );
    // VEILIGHEIDSSLOT: een lege standaardconfig mag NOOIT een gevulde config op de
    // server overschrijven. Voorkomt verlies van koelunits/leveranciers/taken enz.
    if (cfgLeeg(data) && existing?.length && existing[0]?.instellingen && !cfgLeeg(existing[0].instellingen)) {
      console.warn("[HACCP] config-save geblokkeerd: lege config zou een gevulde config overschrijven.");
      return false;
    }
    if (existing?.length) {
      await _sb.update("haccp_config", `tenant_id=eq.${_tid}`, {
        instellingen: data,
        updated_at: new Date().toISOString(),
      });
    } else {
      await _sb.insert("haccp_config", { tenant_id: _tid, instellingen: data });
    }
    return true;
  } catch (e) {
    console.error("HACCP: config opslaan mislukt —", e);
    return false;
  }
}

const ALLERGENEN = [
  "Gluten","Schaaldieren","Eieren","Vis","Pindas","Soja","Melk","Noten",
  "Selderij","Mosterd","Sesamzaad","Zwaveldioxide","Lupine","Weekdieren",
];
const GLUTEN_SUB = ["Tarwe", "Rogge", "Gerst", "Haver"];
const NOTEN_SUB = ["Amandelen", "Hazelnoten", "Walnoten", "Cashewnoten", "Pecannoten", "Paranoten", "Pistachenoten", "Macadamianoten"];
const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const todayDay = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
const todayStr = new Date().toLocaleDateString("nl-NL", {
  weekday: "long",
  day: "numeric",
  month: "long",
});
const uid = () => Date.now() + Math.floor(Math.random() * 10000);

// TABS alleen nog als lookup voor de header-titel — geen navigatie meer
const TABS = [
  { id: "schoonmaak", label: "Schoonmaak", icon: "🧹" },
  { id: "temp",       label: "Temperatuur", icon: "🌡" },
  { id: "kalibratie", label: "Thermometercontrole", icon: "🎯" },
  { id: "ontvangst",  label: "Ontvangst",   icon: "📦" },
  { id: "terugkoel",  label: "Terugkoelen", icon: "❄️" },
  { id: "allergen",   label: "Allergenen",  icon: "⚠️" },
  { id: "afwijking",  label: "Afwijkingen", icon: "🚨" },
  { id: "hygiene",    label: "Hygiëne",     icon: "🤒" },
];

const TERUGKOEL_MAX_MIN = 5 * 60;
function duurInMinuten(startTijd, eindTijd) {
  const p = (t) => {
    if (!t || typeof t !== "string" || !t.includes(":")) return null;
    const [h, m] = t.split(":").map((x) => parseInt(x, 10));
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };
  const s = p(startTijd), e = p(eindTijd);
  if (s === null || e === null) return null;
  let diff = e - s;
  if (diff < 0) diff += 24 * 60;
  return diff;
}
function duurTekst(min) {
  if (min === null || min === undefined) return "";
  return `${Math.floor(min / 60)}u${String(min % 60).padStart(2, "0")}`;
}
function minutenSindsStart(r, now = new Date()) {
  const basis = r?.created_at ? new Date(r.created_at) : null;
  if (!basis || isNaN(basis.getTime())) return null;
  return Math.max(0, Math.floor((now - basis) / (1000 * 60)));
}
function tijdNuHHMM(d = new Date()) {
  return d.toTimeString().slice(0, 5);
}

const C = {
  bg: "#F8FAFC",
  bg2: "#FFFFFF",
  bg3: "#F1F5F9",
  hdr: "#0F172A",
  hdrBdr: "#1E293B",
  txt: "#0F172A",
  txt2: "#475569",
  txt3: "#94A3B8",
  bdr: "#E2E8F0",
  bdr2: "#CBD5E1",
  primary: "#2563EB",
  primBg: "#EFF6FF",
  primBd: "#BFDBFE",
  gold: "#2563EB",
  goldBg: "#EFF6FF",
  goldBd: "#BFDBFE",
  green: "#16A34A",
  greenBg: "#DCFCE7",
  greenBd: "#BBF7D0",
  red: "#DC2626",
  redBg: "#FEE2E2",
  redBd: "#FCA5A5",
  orange: "#EA580C",
  orangeBg: "#FFEDD5",
  orangeBd: "#FED7AA",
};

const INIT_CONFIG = {
  koelunits: [],
  medewerkers: [],
  leveranciers: [],
  thermometers: [],
  schoonmaakTaken: { dagelijks: [], wekelijks: [], vierweeks: [] },
  allergenenGerechten: [],
};
const mergeCfg = (remote) => ({
  ...INIT_CONFIG,
  ...remote,
  schoonmaakTaken: {
    ...INIT_CONFIG.schoonmaakTaken,
    ...(remote?.schoonmaakTaken || {}),
  },
  koelunits: remote?.koelunits || [],
  medewerkers: remote?.medewerkers || [],
  leveranciers: remote?.leveranciers || [],
  thermometers: remote?.thermometers || [],
  allergenenGerechten: remote?.allergenenGerechten || [],
});

function plusEenMaandISO(datum = new Date()) {
  const d = new Date(datum);
  d.setMonth(d.getMonth() + 1);
  return d.toISOString();
}

function thermometerVoor(cfg, id) {
  if (!id) return null;
  return (cfg?.thermometers || []).find((t) => String(t.id) === String(id)) || null;
}

function corrigeerTemperatuur(gemeten, thermometer) {
  const raw = Number(gemeten);
  if (!Number.isFinite(raw)) return { waarde: raw, correctie: 0, actief: false };
  const cal = thermometer?.calibratie;
  if (!cal?.correctie_actief) return { waarde: raw, correctie: 0, actief: false };
  const a = Number(cal.a);
  const b = Number(cal.b);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return { waarde: raw, correctie: 0, actief: false };
  const waarde = Math.round((a * raw + b) * 10) / 10;
  return { waarde, correctie: Math.round((waarde - raw) * 10) / 10, actief: true };
}

function temperatuurDetail(record, veld = "temp") {
  const gemeten = record?.[veld + "_gemeten"];
  const corr = record?.[veld + "_correctie"];
  if (gemeten == null || !corr) return "";
  const teken = Number(corr) > 0 ? "+" : "";
  return ` · gemeten ${gemeten}°C · correctie ${teken}${corr}°C`;
}

function fmtDate(val, opts) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return opts
    ? d.toLocaleDateString("nl-NL", opts)
    : d.toLocaleDateString("nl-NL");
}
function fmtTime(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

// ════════════════════════════════════════════════════════════════════════
// REALTIME HOOK
// Laadt records één keer en houdt ze daarna live in sync tussen devices.
// - Initiële load via sbGet (REST), gefilterd op tenant_id + gesorteerd.
// - Subscription op INSERT/UPDATE/DELETE van de tabel, gefilterd op tenant_id.
// - Dedupliceert op id, zodat een eigen optimistische insert niet dubbel komt.
// Geeft [records, setRecords, loading] terug zodat bestaande optimistische
// save/delete-code (setRecords(...)) blijft werken.
// ════════════════════════════════════════════════════════════════════════
function useRealtimeRecords(table, limit) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1) Initiële load
  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      const d = await sbGet(table, `limit=${limit}`);
      if (alive) {
        setRecords(Array.isArray(d) ? d : []);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [table, limit, _tid]);

  // 2) Realtime subscription
  useEffect(() => {
    if (!_rt || !_tid) return;
    const channel = _rt
      .channel(`rt_${table}_${_tid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table, filter: `tenant_id=eq.${_tid}` },
        (payload) => {
          setRecords((cur) => {
            if (payload.eventType === "INSERT") {
              const row = payload.new;
              if (!row || cur.some((x) => x.id === row.id)) return cur; // eigen insert: overslaan
              return [row, ...cur];
            }
            if (payload.eventType === "UPDATE") {
              const row = payload.new;
              if (!row) return cur;
              return cur.map((x) => (x.id === row.id ? { ...x, ...row } : x));
            }
            if (payload.eventType === "DELETE") {
              const goneId = payload.old?.id;
              if (goneId == null) return cur;
              return cur.filter((x) => x.id !== goneId);
            }
            return cur;
          });
        }
      )
      .subscribe();
    return () => { try { _rt.removeChannel(channel); } catch {} };
  }, [table, _tid]);

  return [records, setRecords, loading];
}

// ════════════════════════════════════════════════════════════════════════
// HACCP MODULE
// Props: sb, tenantId, activeTab (van schil), onTabChange (van schil)
// ════════════════════════════════════════════════════════════════════════
export default function HaccpModule({ sb, tenantId, activeTab, onTabChange, supabaseClient, coolingRequest, onCoolingRequestConsumed }) {
  const [cfg, setCfg] = useState(INIT_CONFIG);
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rapportOpen, setRapportOpen] = useState(false);
  const applyingRemoteRef = useRef(false); // voorkomt save-echo lus op config

  // Medewerkers komen uit de centrale Team-module (team_data in Supabase).
  // Init uit localStorage als snelle terugval; wordt zo uit team_data ververst.
  // Stond eerder ALLEEN in localStorage (kc-team-v1), die pas gevuld wordt zodra
  // de Team-module geopend is — in een vers venster of na refresh bleef de
  // keuzelijst daardoor leeg.
  const [teamMedewerkers, setTeamMedewerkers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kc-team-v1") || "{}")?.medewerkers || []; } catch { return []; }
  });

  const tab = activeTab || "schoonmaak";

  setBackend(sb, tenantId, supabaseClient);

  useEffect(() => {
    if (!tenantId) return;
    setBackend(sb, tenantId, supabaseClient);
    (async () => {
      const remote = await sbLoadConfig();
      if (remote) {
        const merged = mergeCfg(remote);
        setCfg(merged);
        migreerMedewerkers(merged.medewerkers);
      }
      setReady(true);
    })();
  }, [sb, tenantId]);

  // Medewerkers ophalen uit team_data (Supabase) — de centrale bron. Zo is de
  // keuzelijst ook gevuld zonder dat de Team-module eerst geopend is.
  useEffect(() => {
    if (!sb || !tenantId) return;
    (async () => {
      try {
        const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
        const meds = rows?.[0]?.data?.medewerkers;
        if (Array.isArray(meds)) setTeamMedewerkers(meds);
      } catch { /* stil — terugval op localStorage-init blijft staan */ }
    })();
  }, [sb, tenantId]);

  useEffect(() => {
    if (!ready) return;
    if (applyingRemoteRef.current) { applyingRemoteRef.current = false; return; } // remote apply: niet terugschrijven
    setSyncing(true);
    const t = setTimeout(async () => {
      await sbSaveConfig(cfg);
      setSyncing(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [cfg, ready]);

  // Realtime: luister op haccp_config wijzigingen van andere devices
  useEffect(() => {
    if (!supabaseClient || !tenantId || !ready) return;
    const channel = supabaseClient
      .channel(`haccp_config_${tenantId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "haccp_config",
        filter: `tenant_id=eq.${tenantId}`,
      }, async () => {
        // Herlaad config van Supabase (zonder een nieuwe save te triggeren)
        const remote = await sbLoadConfig();
        if (remote) { applyingRemoteRef.current = true; setCfg(mergeCfg(remote)); }
      })
      .subscribe();
    return () => supabaseClient.removeChannel(channel);
  }, [supabaseClient, tenantId, ready]);

  const updateCfg = (fn) => setCfg((c) => ({ ...c, ...fn(c) }));

  // cfg met de medewerkers uit de centrale Team-module (team_data). Zijn die er,
  // dan gebruiken we die; anders valt 'ie terug op cfg.medewerkers (oude bron uit
  // haccp_config). Dit gaat naar alle tabs met een medewerker-keuzelijst.
  const cfgMetTeam = (teamMedewerkers && teamMedewerkers.length > 0)
    ? { ...cfg, medewerkers: teamMedewerkers }
    : cfg;

  if (!ready)
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          background: C.bg,
          color: C.txt2,
          fontFamily: "DM Sans, system-ui, sans-serif",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 32 }}>🌡️</div>
        <div>HACCP laden…</div>
      </div>
    );

  const activeTabDef = TABS.find((t) => t.id === tab);

  return (
    <div
      className="kc-haccp"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: C.bg,
        fontFamily: "DM Sans, system-ui, sans-serif",
        fontSize: 13,
        color: C.txt,
        overflow: "hidden",
      }}
    >
      <style>{CSS}</style>

      {/* ── Header — alleen titel + sync + datum, GEEN tabbar ── */}
      <header
        style={{
          background: C.hdr,
          borderBottom: `1px solid ${C.hdrBdr}`,
          flexShrink: 0,
          height: 52,
          display: "flex",
          alignItems: "center",
          padding: "0 18px",
          gap: 12,
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: 0.3,
          }}
        >
          {activeTabDef?.icon} {activeTabDef?.label || "HACCP"}
        </span>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: syncing ? C.gold : "rgba(255,255,255,.55)",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: syncing ? C.gold : C.green,
            }}
          />
          {syncing ? "synchroniseren…" : "gesynchroniseerd"}
        </div>
        <button
          onClick={() => setRapportOpen(true)}
          style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: 600 }}
        >
          📄 NVWA Rapport
        </button>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>
          {todayStr}
        </div>
      </header>

      {/* ── NVWA Rapport modal ── */}
      {rapportOpen && (
        <RapportModal
          sb={_sb}
          tenantId={_tid}
          cfg={cfg}
          onClose={() => setRapportOpen(false)}
        />
      )}

      {/* ── Content — geen tabbar meer, direct het scherm ── */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {tab === "temp" && <TempTab cfg={cfgMetTeam} updateCfg={updateCfg} />}
        {tab === "kalibratie" && <ThermometerControleTab cfg={cfgMetTeam} updateCfg={updateCfg} />}
        {tab === "ontvangst" && <OntvangstTab cfg={cfgMetTeam} updateCfg={updateCfg} />}
        {tab === "terugkoel" && <TerugkoelTab cfg={cfgMetTeam} coolingRequest={coolingRequest} onCoolingRequestConsumed={onCoolingRequestConsumed} />}
        {tab === "schoonmaak" && <SchoonmaakTab cfg={cfgMetTeam} updateCfg={updateCfg} />}
        {tab === "allergen" && <AllergenenTab cfg={cfg} updateCfg={updateCfg} />}
        {tab === "afwijking" && <AfwijkingTab cfg={cfgMetTeam} />}
        {tab === "hygiene" && <HygieneTab cfg={cfgMetTeam} />}
      </div>
    </div>
  );
}

// KC_UI_V2_HACCP_INLINE_BEHEER
function HaccpInlineBeheer({ soort, cfg, updateCfg }) {
  const { requireBeheer } = useBeheerPin();
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState("");
  const [type, setType] = useState("koel");
  const [freq, setFreq] = useState("dagelijks");
  const [editId, setEditId] = useState(null);
  const [editNaam, setEditNaam] = useState("");
  const [editType, setEditType] = useState("koel");

  const meta = soort === "units"
    ? { titel: "Koel-/vriesunits beheren", knop: "+ Unit toevoegen", leeg: "Nog geen koel- of vriesunits ingesteld." }
    : soort === "leveranciers"
      ? { titel: "Leveranciers beheren", knop: "+ Leverancier toevoegen", leeg: "Nog geen leveranciers ingesteld." }
      : { titel: "Schoonmaaktaken beheren", knop: "+ Taak toevoegen", leeg: "Nog geen schoonmaaktaken ingesteld." };

  const beginEdit = (item, extra = {}) => {
    setEditId(item.id);
    setEditNaam(item.naam || "");
    setEditType(extra.type || item.type || "koel");
  };

  const toevoegen = async () => {
    const n = naam.trim();
    if (!n) return;
    if (!(await requireBeheer(`${meta.knop.replace("+ ", "").toLowerCase()}`))) return;
    if (soort === "units") updateCfg(c => ({ koelunits: [...(c.koelunits || []), { id: uid(), naam: n, type }] }));
    if (soort === "leveranciers") {
      if ((cfg.leveranciers || []).some(x => String(x.naam || "").toLowerCase() === n.toLowerCase())) return;
      updateCfg(c => ({ leveranciers: [...(c.leveranciers || []), { id: uid(), naam: n }] }));
    }
    if (soort === "taken") updateCfg(c => ({ schoonmaakTaken: { ...(c.schoonmaakTaken || {}), [freq]: [...(c.schoonmaakTaken?.[freq] || []), { id: uid(), naam: n }] } }));
    setNaam("");
  };

  const opslaan = async (item, itemFreq = null) => {
    const n = editNaam.trim();
    if (!n) return;
    if (!(await requireBeheer("HACCP-inrichting te wijzigen"))) return;
    if (soort === "units") updateCfg(c => ({ koelunits: (c.koelunits || []).map(x => x.id === item.id ? { ...x, naam: n, type: editType } : x) }));
    if (soort === "leveranciers") updateCfg(c => ({ leveranciers: (c.leveranciers || []).map(x => x.id === item.id ? { ...x, naam: n } : x) }));
    if (soort === "taken" && itemFreq) updateCfg(c => ({ schoonmaakTaken: { ...(c.schoonmaakTaken || {}), [itemFreq]: (c.schoonmaakTaken?.[itemFreq] || []).map(x => x.id === item.id ? { ...x, naam: n } : x) } }));
    setEditId(null);
  };

  const verwijderen = async (item, itemFreq = null) => {
    if (!(await requireBeheer("HACCP-inrichting te verwijderen"))) return;
    if (soort === "units") updateCfg(c => ({ koelunits: (c.koelunits || []).filter(x => x.id !== item.id) }));
    if (soort === "leveranciers") updateCfg(c => ({ leveranciers: (c.leveranciers || []).filter(x => x.id !== item.id) }));
    if (soort === "taken" && itemFreq) updateCfg(c => ({ schoonmaakTaken: { ...(c.schoonmaakTaken || {}), [itemFreq]: (c.schoonmaakTaken?.[itemFreq] || []).filter(x => x.id !== item.id) } }));
  };

  const lijst = soort === "units" ? (cfg.koelunits || []) : soort === "leveranciers" ? (cfg.leveranciers || []) : null;
  const taakFreqs = [
    ["dagelijks", "Dagelijks"], ["wekelijks", "Wekelijks"], ["vierweeks", "4-wekelijks"]
  ];

  const rij = (item, itemFreq = null) => (
    <div key={`${itemFreq || soort}-${item.id}`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.bdr}` }}>
      {editId === item.id ? (
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          <input style={{ ...inputStyle, flex: "1 1 180px" }} value={editNaam} onChange={e => setEditNaam(e.target.value)} />
          {soort === "units" && <select style={{ ...inputStyle, width: 130 }} value={editType} onChange={e => setEditType(e.target.value)}><option value="koel">Koeling</option><option value="vriezer">Vriezer</option></select>}
          <button onClick={() => opslaan(item, itemFreq)} style={{ minHeight: 34, border: 0, borderRadius: 6, background: C.primary, color: "#fff", padding: "0 12px", fontWeight: 700, cursor: "pointer" }}>Opslaan</button>
          <button onClick={() => setEditId(null)} style={{ minHeight: 34, border: `1px solid ${C.bdr}`, borderRadius: 6, background: C.bg2, color: C.txt2, padding: "0 10px", fontWeight: 700, cursor: "pointer" }}>Annuleren</button>
        </div>
      ) : (
        <div><strong>{item.naam}</strong>{soort === "units" && <span style={{ color: C.txt3, marginLeft: 8, fontSize: 11.5 }}>{item.type === "vriezer" ? "Vriezer" : "Koeling"}</span>}</div>
      )}
      {editId !== item.id && <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => beginEdit(item)} style={{ border: `1px solid ${C.bdr}`, background: C.bg2, borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: C.txt2, fontWeight: 700 }}>Wijzig</button>
        <button onClick={() => verwijderen(item, itemFreq)} style={{ border: `1px solid ${C.redBd}`, background: C.redBg, borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: C.red, fontWeight: 700 }}>Verwijder</button>
      </div>}
    </div>
  );

  // KC_UI_V2_HACCP_BEHEER_MODAL
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", border: `1px solid ${C.bdr}`, borderRadius: 9, background: C.primBg }}>
        <div>
          <div style={{ fontWeight: 800, color: C.txt }}>{meta.titel}</div>
          <div style={{ fontSize: 11.5, color: C.txt3, marginTop: 2 }}>Toevoegen, wijzigen en verwijderen vraagt de beheer-pincode.</div>
        </div>
        <button type="button" onClick={() => setOpen(true)} style={{ minHeight: 36, border: 0, borderRadius: 7, background: C.primary, color: "#fff", padding: "0 14px", fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
          {soort === "units" ? "+ Unit beheren" : soort === "leveranciers" ? "+ Leverancier beheren" : "+ Taak beheren"}
        </button>
      </div>

      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(15,23,42,.48)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }} onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div style={{ width: "min(760px, 100%)", maxHeight: "85vh", overflowY: "auto", background: C.bg2, borderRadius: 14, boxShadow: "0 24px 60px rgba(15,23,42,.28)", border: `1px solid ${C.bdr}` }}>
            <div style={{ position: "sticky", top: 0, zIndex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 16px", background: C.bg2, borderBottom: `1px solid ${C.bdr}` }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.txt }}>{meta.titel}</div>
                <div style={{ fontSize: 11.5, color: C.txt3, marginTop: 2 }}>Beheer van HACCP-inrichting</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} style={{ border: `1px solid ${C.bdr}`, background: C.bg2, borderRadius: 7, padding: "7px 11px", color: C.txt2, fontWeight: 800, cursor: "pointer" }}>Sluiten</button>
            </div>

            <div style={{ padding: 16 }}>
              {soort === "taken" ? taakFreqs.map(([f, label]) => (
                <div key={f} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.txt2, marginBottom: 4 }}>{label}</div>
                  {(cfg.schoonmaakTaken?.[f] || []).length
                    ? (cfg.schoonmaakTaken?.[f] || []).map(item => rij(item, f))
                    : <div style={{ color: C.txt3, fontSize: 12, padding: "7px 0" }}>Geen taken</div>}
                </div>
              )) : (
                lijst?.length
                  ? lijst.map(item => rij(item))
                  : <div style={{ color: C.txt3, fontSize: 12, padding: "4px 0 10px" }}>{meta.leeg}</div>
              )}

              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.bdr}` }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.txt2, marginBottom: 8 }}>
                  {soort === "units" ? "Nieuwe koel-/vriesunit" : soort === "leveranciers" ? "Nieuwe leverancier" : "Nieuwe schoonmaaktaak"}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input style={{ ...inputStyle, flex: "1 1 240px" }} value={naam} onChange={e => setNaam(e.target.value)} placeholder={soort === "units" ? "Naam unit" : soort === "leveranciers" ? "Naam leverancier" : "Naam schoonmaaktaak"} onKeyDown={e => e.key === "Enter" && toevoegen()} />
                  {soort === "units" && <select style={{ ...inputStyle, width: 140 }} value={type} onChange={e => setType(e.target.value)}><option value="koel">Koeling</option><option value="vriezer">Vriezer</option></select>}
                  {soort === "taken" && <select style={{ ...inputStyle, width: 150 }} value={freq} onChange={e => setFreq(e.target.value)}><option value="dagelijks">Dagelijks</option><option value="wekelijks">Wekelijks</option><option value="vierweeks">4-wekelijks</option></select>}
                  <button type="button" onClick={toevoegen} style={{ minHeight: 36, border: 0, borderRadius: 7, background: C.primary, color: "#fff", padding: "0 15px", fontWeight: 800, cursor: "pointer" }}>
                    {soort === "units" ? "Unit toevoegen" : soort === "leveranciers" ? "Leverancier toevoegen" : "Taak toevoegen"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════
// THERMOMETERCONTROLE
// ════════════════════════════════════════════════════════════════════════
function ThermometerSelect({ cfg, value, onChange, error = false }) {
  const lijst = cfg?.thermometers || [];
  return (
    <select
      style={{ ...inputStyle, ...(error ? { borderColor: C.red, borderWidth: 2, background: C.redBg } : {}) }}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Kies thermometer…</option>
      {lijst.map((t) => <option key={t.id} value={t.id}>{t.naam}</option>)}
    </select>
  );
}

function ThermometerControleTab({ cfg, updateCfg }) {
  const [records, setRecords, loading] = useRealtimeRecords("haccp_thermometer_calibratie", 100);
  const [geselecteerd, setGeselecteerd] = useState(() => cfg?.thermometers?.[0]?.id || "");
  const [nieuweNaam, setNieuweNaam] = useState("");
  const [form, setForm] = useState({ ijs: "", kook: "", afwijking: null, medewerker: "", opmerking: "" });
  const [attentie, setAttentie] = useState(false);
  const [opslaanFout, setOpslaanFout] = useState("");
  const [toonUitleg, setToonUitleg] = useState(false);
  const [toonHistorie, setToonHistorie] = useState(false);

  useEffect(() => {
    if (!geselecteerd && cfg?.thermometers?.length) setGeselecteerd(cfg.thermometers[0].id);
  }, [cfg?.thermometers, geselecteerd]);

  const thermometer = thermometerVoor(cfg, geselecteerd);
  const laatste = thermometer?.calibratie || null;
  const volgende = laatste?.volgende_controle ? new Date(laatste.volgende_controle) : null;
  const verlopen = volgende && !Number.isNaN(volgende.getTime()) && volgende < new Date();
  const status = !laatste
    ? { tekst: "Nog niet gecontroleerd", tone: "open" }
    : verlopen
      ? { tekst: "Controle verlopen", tone: "bad" }
      : laatste.correctie_actief
        ? { tekst: "Correctie actief", tone: "open" }
        : { tekst: "In orde", tone: "ok" };

  const voegThermometerToe = () => {
    const naam = nieuweNaam.trim();
    if (!naam) return;
    const bestaand = (cfg.thermometers || []).find((t) => String(t.naam || "").toLowerCase() === naam.toLowerCase());
    if (bestaand) { setGeselecteerd(bestaand.id); setNieuweNaam(""); return; }
    const id = String(uid());
    updateCfg((cur) => ({ thermometers: [...(cur.thermometers || []), { id, naam, calibratie: null }] }));
    setGeselecteerd(id);
    setNieuweNaam("");
  };

  const compleet = !!geselecteerd && !!form.medewerker && form.ijs !== "" && form.kook !== "" && form.afwijking !== null;

  const slaControleOp = async () => {
    if (!compleet) { setAttentie(true); return; }
    setOpslaanFout("");
    const ijs = parseFloat(String(form.ijs).replace(",", "."));
    const kook = parseFloat(String(form.kook).replace(",", "."));
    if (!Number.isFinite(ijs) || !Number.isFinite(kook) || kook - ijs < 50) {
      setOpslaanFout("Controleer de ingevulde ijs- en kookwatermetingen.");
      return;
    }

    const correctieActief = form.afwijking === true;
    const a = correctieActief ? 100 / (kook - ijs) : 1;
    const b = correctieActief ? -a * ijs : 0;
    const nu = new Date();
    const volgendeControle = plusEenMaandISO(nu);

    const payload = {
      thermometer_id: String(geselecteerd),
      thermometer_naam: thermometer?.naam || "Thermometer",
      medewerker: form.medewerker,
      ijs_gemeten: ijs,
      kook_gemeten: kook,
      afwijking: form.afwijking === true,
      correctie_actief: correctieActief,
      volgende_controle: volgendeControle.slice(0, 10),
      opmerking: form.opmerking || "",
      created_at: nu.toISOString(),
    };

    // Eerst de HACCP-registratie opslaan. Pas als die gelukt is, activeren we
    // de correctie in de config. Zo kan een ontbrekende tabel/RLS-fout niet
    // leiden tot "correctie actief" zonder bijbehorende historie.
    const rec = await sbPost("haccp_thermometer_calibratie", payload);
    if (!rec) {
      setOpslaanFout("De controle kon niet in de HACCP-historie worden opgeslagen. Controleer of de Supabase-migratie voor thermometercontrole is uitgevoerd.");
      return;
    }

    updateCfg((cur) => ({
      thermometers: (cur.thermometers || []).map((t) => String(t.id) === String(geselecteerd)
        ? {
            ...t,
            calibratie: {
              ijs_gemeten: ijs,
              kook_gemeten: kook,
              correctie_actief: correctieActief,
              a,
              b,
              gecontroleerd_op: nu.toISOString(),
              volgende_controle: volgendeControle,
              medewerker: form.medewerker,
            },
          }
        : t),
    }));

    setRecords((r) => [rec, ...r]);
    setForm((f) => ({ ...f, ijs: "", kook: "", afwijking: null, opmerking: "" }));
    setAttentie(false);
  };

  const historie = geselecteerd ? records.filter((r) => String(r.thermometer_id) === String(geselecteerd)) : records;
  const zichtbaar = toonHistorie ? historie.slice(0, 30) : historie.slice(0, 5);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <TempStatusCard label="Status" value={status.tekst} tone={status.tone} />
        <TempStatusCard label="Laatste controle" value={laatste?.gecontroleerd_op ? fmtDate(laatste.gecontroleerd_op) : "-"} tone="open" />
        <TempStatusCard label="Volgende controle" value={laatste?.volgende_controle ? fmtDate(laatste.volgende_controle) : "-"} tone={verlopen ? "bad" : "open"} />
      </div>

      <Section title="Thermometer">
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {(cfg.thermometers || []).length > 0 && (
            <ThermometerSelect cfg={cfg} value={geselecteerd} onChange={setGeselecteerd} error={attentie && !geselecteerd} />
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input style={{ ...inputStyle, flex: "1 1 220px" }} placeholder="Nieuwe thermometer, bijv. Keuken 1" value={nieuweNaam} onChange={(e) => setNieuweNaam(e.target.value)} />
            <button type="button" onClick={voegThermometerToe} disabled={!nieuweNaam.trim()}
              style={{ minHeight: 38, borderRadius: 8, border: 0, background: nieuweNaam.trim() ? C.primary : C.bdr, color: nieuweNaam.trim() ? "#fff" : C.txt3, padding: "0 15px", fontWeight: 800, cursor: nieuweNaam.trim() ? "pointer" : "default" }}>
              Toevoegen
            </button>
          </div>
        </div>
      </Section>

      <Section title="Maandelijkse controle">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button type="button" onClick={() => setToonUitleg((v) => !v)}
            style={{ alignSelf: "flex-start", border: 0, background: "none", color: C.primary, fontWeight: 800, cursor: "pointer", padding: 0 }}>
            {toonUitleg ? "Uitleg verbergen" : "Hoe voer ik de controle uit?"}
          </button>
          {toonUitleg && (
            <div style={{ padding: 12, borderRadius: 9, border: `1px solid ${C.primBd}`, background: C.primBg, color: C.txt2, lineHeight: 1.6, fontSize: 12.5 }}>
              <strong>IJswater:</strong> vul een glas ruim met ijs, voeg een beetje koud water toe, roer en meet zonder bodem of wand te raken. Referentie: 0,0°C.<br />
              <strong>Kokend water:</strong> meet in volledig kokend water zonder bodem of panwand te raken. Rond zeeniveau is de referentie ongeveer 100,0°C.
            </div>
          )}
          <FormRow label="Thermometer"><ThermometerSelect cfg={cfg} value={geselecteerd} onChange={setGeselecteerd} error={attentie && !geselecteerd} /></FormRow>
          <FormRow label="IJswater gemeten °C"><input type="number" step="0.1" style={inputStyle} value={form.ijs} onChange={(e) => setForm((f) => ({ ...f, ijs: e.target.value }))} placeholder="bijv. 0.8" /></FormRow>
          <FormRow label="Kokend water gemeten °C"><input type="number" step="0.1" style={inputStyle} value={form.kook} onChange={(e) => setForm((f) => ({ ...f, kook: e.target.value }))} placeholder="bijv. 100.9" /></FormRow>
          <FormRow label="Afwijking?"><ToggleBtn value={form.afwijking} onChange={(v) => setForm((f) => ({ ...f, afwijking: v }))} error={attentie && form.afwijking === null} /></FormRow>
          <FormRow label="Medewerker"><MedewerkerSelect cfg={cfg} value={form.medewerker} onChange={(v) => setForm((f) => ({ ...f, medewerker: v }))} error={attentie && !form.medewerker} /></FormRow>
          <FormRow label="Opmerking"><input style={inputStyle} value={form.opmerking} onChange={(e) => setForm((f) => ({ ...f, opmerking: e.target.value }))} placeholder="Optioneel…" /></FormRow>
          {form.afwijking === true && (
            <div style={{ padding: "10px 12px", borderRadius: 8, background: C.orangeBg, border: `1px solid ${C.orangeBd}`, color: C.orange, fontSize: 12, fontWeight: 700 }}>
              Na opslaan past KitchenControl toekomstige metingen met deze thermometer automatisch aan. De ruwe meting blijft bewaard.
            </div>
          )}
          {opslaanFout && (
            <div style={{ padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.redBd}`, background: C.redBg, color: C.red, fontSize: 12.5, fontWeight: 700 }}>
              {opslaanFout}
            </div>
          )}
          <SaveBtn onClick={slaControleOp} disabled={!compleet} onInvalid={() => setAttentie(true)} />
        </div>
      </Section>

      <Section title={toonHistorie ? "Historie (laatste 30)" : "Historie (laatste 5)"}>
        {loading ? <div style={{ color: C.txt3 }}>Laden…</div> : zichtbaar.length === 0 ? <Empty text="Nog geen thermometercontroles vastgelegd." /> : (
          <>
            {zichtbaar.map((r) => (
              <RecordRow key={r.id}
                afwijking={r.afwijking}
                left={<><b>{r.thermometer_naam}</b> · ijs {r.ijs_gemeten}°C · kook {r.kook_gemeten}°C</>}
                right={r.medewerker}
                sub={`${fmtDate(r.created_at)} · ${r.correctie_actief ? "correctie actief" : "geen correctie"}${r.opmerking ? ` · ${r.opmerking}` : ""}`}
                onDelete={() => { sbDelete("haccp_thermometer_calibratie", r.id); setRecords((rs) => rs.filter((x) => x.id !== r.id)); }}
              />
            ))}
            {historie.length > 5 && (
              <button onClick={() => setToonHistorie((v) => !v)} style={{ marginTop: 8, minHeight: 38, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.bg2, color: C.primary, fontFamily: "inherit", fontWeight: 700, cursor: "pointer" }}>
                {toonHistorie ? "Minder tonen" : `Meer tonen (${Math.min(historie.length, 30) - 5})`}
              </button>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TEMPERATUUR TAB
// ════════════════════════════════════════════════════════════════════════
const MAATREGELEN = ["Deur gecontroleerd", "Thermostaat bijgesteld", "Producten verplaatst", "Technische dienst gebeld"];

function TempStatusCard({ label, value, tone = "open" }) {
  const kleur =
    tone === "bad"
      ? { bg: C.redBg, bd: C.redBd, fg: C.red }
      : tone === "ok"
        ? { bg: C.greenBg, bd: C.greenBd, fg: C.green }
        : { bg: C.bg2, bd: C.bdr, fg: C.txt2 };
  return (
    <div style={{ border: `1px solid ${kleur.bd}`, borderRadius: 8, background: kleur.bg, padding: "10px 12px", minHeight: 58 }}>
      <div style={{ fontSize: 11, color: C.txt3, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0 }}>{label}</div>
      <div style={{ marginTop: 4, color: kleur.fg, fontSize: 16, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function TempTab({ cfg, updateCfg }) {
  const [records, setRecords, loading] = useRealtimeRecords("haccp_temp", 100);
  const [form, setForm] = useState({
    unit_id: "",
    unit_naam: "",
    unit_type: "koel",
    temp: "",
    thermometer_id: cfg?.thermometers?.length === 1 ? cfg.thermometers[0].id : "",
    medewerker: "",
    opmerking: "",
  });
  const [activeType, setActiveType] = useState("koel");
  const [attentie, setAttentie] = useState(false);
  const [toonMeerHistorie, setToonMeerHistorie] = useState(false);

  const save = async () => {
    if (!form.unit_id || !form.temp || !form.medewerker) return;
    const unit = (cfg.koelunits || []).find(
      (u) => String(u.id) === String(form.unit_id)
    );
    const unitNaam = form.unit_naam || unit?.naam || form.unit_id;
    const unitType = form.unit_type || unit?.type || "koel";
    const tempGemeten = parseFloat(String(form.temp).replace(",", "."));
    const thermometer = thermometerVoor(cfg, form.thermometer_id);
    const gecorrigeerd = corrigeerTemperatuur(tempGemeten, thermometer);
    const tempNum = gecorrigeerd.waarde;
    const afwijking = tempIsAfwijking(unitType, tempNum);
    const payload = {
      unit_id: form.unit_id,
      unit_naam: unitNaam,
      unit_type: unitType,
      temp: tempNum,
      temp_gemeten: Number.isFinite(tempGemeten) ? tempGemeten : null,
      temp_correctie: gecorrigeerd.correctie,
      thermometer_id: form.thermometer_id || null,
      norm: unitType === "vriezer" ? -18 : 7,
      afwijking,
      medewerker: form.medewerker,
      opmerking: form.opmerking,
      dag: todayDay,
      created_at: new Date().toISOString(),
    };
    const rec = await sbPost("haccp_temp", payload);
    if (rec) setRecords((r) => [{ ...payload, id: rec.id }, ...r]);
    setForm((f) => ({
      ...f,
      temp: String((f.unit_type || "koel") === "vriezer" ? -18 : 4),
      opmerking: "",
    }));
  };

  const alleUnits = cfg.koelunits || [];
  const koelunits = alleUnits.filter((u) => u.type === activeType);
  const today = records.filter((r) => r.dag === todayDay);
  const vandaagUnitIds = new Set(today.map((r) => String(r.unit_id)).filter(Boolean));
  const koelVandaag = alleUnits.filter((u) => u.type === "koel" && vandaagUnitIds.has(String(u.id))).length;
  const vriesVandaag = alleUnits.filter((u) => u.type === "vriezer" && vandaagUnitIds.has(String(u.id))).length;
  const koelTotaal = alleUnits.filter((u) => u.type === "koel").length;
  const vriesTotaal = alleUnits.filter((u) => u.type === "vriezer").length;
  const afwijkingenVandaag = today.filter((r) => r.afwijking).length;
  const historie = records.filter((r) => r.dag !== todayDay);
  const historieZichtbaar = toonMeerHistorie ? historie.slice(0, 50) : historie.slice(0, 5);
  const mistUnit = !form.unit_id, mistMedewerker = !form.medewerker;
  const thermometerVerplicht = (cfg.thermometers || []).length > 0;
  const mistThermometer = thermometerVerplicht && !form.thermometer_id;
  const compleet = !mistUnit && !mistMedewerker && !mistThermometer && !!form.temp;
  const tempGemetenPreview = parseFloat(String(form.temp).replace(",", "."));
  const tempPreview = corrigeerTemperatuur(tempGemetenPreview, thermometerVoor(cfg, form.thermometer_id));
  const isAfw = !mistUnit && tempIsAfwijking(form.unit_type || activeType, tempPreview.waarde);
  useEffect(() => {
    if (compleet && attentie) setAttentie(false);
  }, [compleet, attentie]);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <HaccpInlineBeheer soort="units" cfg={cfg} updateCfg={updateCfg} />
      {cfg.koelunits.length === 0 ? (
        <EmptyState icon="🌡️" text="Nog geen koel/vriesunits ingesteld. Voeg hierboven eerst een unit toe." />
      ) : (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            {["koel", "vriezer"].map((t) => (
              <button
                key={t}
                onClick={() => {
                  setActiveType(t);
                  setForm((f) => ({
                    ...f,
                    unit_id: "",
                    unit_naam: "",
                    unit_type: t,
                    temp: String(t === "vriezer" ? -18 : 4),
                  }));
                }}
                style={{
                  padding: "6px 16px",
                  fontSize: 12,
                  fontFamily: "inherit",
                  fontWeight: 600,
                  cursor: "pointer",
                  borderRadius: 4,
                  border: `1px solid ${activeType === t ? C.primary : C.bdr}`,
                  background: activeType === t ? C.primBg : C.bg2,
                  color: activeType === t ? C.primary : C.txt2,
                }}
              >
                {t === "koel" ? "🧊 Koeling" : "❄️ Vriezer"}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            <TempStatusCard label="Vandaag" value={`${today.length} meting${today.length === 1 ? "" : "en"}`} tone={today.length > 0 ? "ok" : "open"} />
            <TempStatusCard label="Koelingen" value={`${koelVandaag}/${koelTotaal} gemeten`} tone={koelTotaal > 0 && koelVandaag >= koelTotaal ? "ok" : "open"} />
            <TempStatusCard label="Vriezers" value={`${vriesVandaag}/${vriesTotaal} gemeten`} tone={vriesTotaal > 0 && vriesVandaag >= vriesTotaal ? "ok" : "open"} />
            <TempStatusCard label="Afwijkingen" value={`${afwijkingenVandaag}`} tone={afwijkingenVandaag > 0 ? "bad" : "ok"} />
          </div>
          <Section title="Nieuwe meting">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 12, color: attentie && mistUnit ? C.red : C.txt2, marginBottom: 8, fontWeight: attentie && mistUnit ? 700 : 400 }}>
                  Unit{attentie && mistUnit ? " — kies een unit" : ""}
                </div>
                {mistUnit && (
                  <div style={{ marginBottom: 8, padding: "10px 12px", borderRadius: 8, border: `1px solid ${attentie ? C.redBd : C.primBd}`, background: attentie ? C.redBg : C.primBg, color: attentie ? C.red : C.primary, fontSize: 12, fontWeight: 700 }}>
                    Kies eerst welke {activeType === "vriezer" ? "vriezer" : "koeling"} je meet.
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                    ...(attentie && mistUnit ? { padding: 8, borderRadius: 10, border: `2px solid ${C.red}`, background: C.redBg } : {}),
                  }}
                >
                  {koelunits.map((u) => {
                    const gekozen = String(form.unit_id) === String(u.id);
                    return (
                      <div
                        key={u.id}
                        onClick={() => setForm((f) => ({ ...f, unit_id: u.id, unit_naam: u.naam, unit_type: u.type || "koel", temp: String((u.type || "koel") === "vriezer" ? -18 : 4) }))}
                        style={{
                          padding: 14,
                          borderRadius: 8,
                          cursor: "pointer",
                          border: gekozen ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`,
                          background: gekozen ? C.primBg : C.bg2,
                          transition: "border-color .12s, background .12s",
                        }}
                      >
                        <strong style={{ fontSize: 14, color: C.txt }}>{u.naam}</strong>
                        <div style={{ fontSize: 11, color: C.txt3, marginTop: 3 }}>
                          Norm: {u.type === "vriezer" ? "≤-18°C" : "≤7°C"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 12, color: C.txt2, marginBottom: 8 }}>Temperatuur °C</div>
                <TempStepper
                  type={form.unit_type || activeType}
                  value={form.temp}
                  onChange={(v) => setForm((f) => ({ ...f, temp: v }))}
                  displayValue={tempPreview.actief ? tempPreview.waarde : null}
                  statusValue={tempPreview.actief ? tempPreview.waarde : null}
                  subtext={tempPreview.actief ? `gemeten ${tempGemetenPreview}°C · correctie ${tempPreview.correctie > 0 ? "+" : ""}${tempPreview.correctie}°C` : ""}
                />
              </div>
              {isAfw && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 12, color: C.orange, fontWeight: 700, marginBottom: 8 }}>⚠️ Afwijking — kies de genomen maatregel(en):</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {MAATREGELEN.map((m) => {
                      const aan = (form.opmerking || "").split("; ").includes(m);
                      return (
                        <button key={m} type="button"
                          onClick={() => setForm((f) => {
                            const parts = (f.opmerking || "").split("; ").filter(Boolean);
                            const next = parts.includes(m) ? parts.filter((p) => p !== m) : [...parts, m];
                            return { ...f, opmerking: next.join("; ") };
                          })}
                          style={{ minHeight: 44, padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, border: aan ? `2px solid ${C.orange}` : `1px solid ${C.orangeBd}`, background: aan ? C.orangeBg : C.bg2, color: C.orange, WebkitTapHighlightColor: "transparent" }}>
                          {m}
                        </button>
                      );
                    })}
                  </div>
                  {!form.opmerking && (
                    <div style={{ marginTop: 8, fontSize: 12, color: C.orange, fontWeight: 600 }}>
                      Noteer kort wat je hebt gecontroleerd of aangepast. Dit komt mee in het NVWA rapport.
                    </div>
                  )}
                </div>
              )}
              {(cfg.thermometers || []).length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 12, color: attentie && mistThermometer ? C.red : C.txt2, marginBottom: 8, fontWeight: attentie && mistThermometer ? 700 : 400 }}>
                    Thermometer{attentie && mistThermometer ? " — kies een thermometer" : ""}
                  </div>
                  {(cfg.thermometers || []).length === 1 ? (
                    <div style={{ padding: "9px 11px", borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.bg2, color: C.txt, fontSize: 13, fontWeight: 600 }}>
                      {(cfg.thermometers || [])[0]?.naam || "Thermometer"}
                    </div>
                  ) : (
                    <ThermometerSelect
                      cfg={cfg}
                      value={form.thermometer_id}
                      onChange={(v) => setForm((f) => ({ ...f, thermometer_id: v }))}
                      error={attentie && mistThermometer}
                    />
                  )}
                </div>
              )}
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 12, color: attentie && mistMedewerker ? C.red : C.txt2, marginBottom: 8, fontWeight: attentie && mistMedewerker ? 700 : 400 }}>
                  Medewerker{attentie && mistMedewerker ? " — kies een medewerker" : ""}
                </div>
                <MedewerkerSelect cfg={cfg} value={form.medewerker} onChange={(v) => setForm((f) => ({ ...f, medewerker: v }))} error={attentie && mistMedewerker} />
              </div>
              <FormRow label="Opmerking">
                <input style={{ ...inputStyle, ...(isAfw && !form.opmerking ? { borderColor: C.orangeBd, background: C.orangeBg } : {}) }} placeholder={isAfw ? "Bij afwijking: maatregel of toelichting..." : "Optioneel..."} value={form.opmerking} onChange={(e) => setForm((f) => ({ ...f, opmerking: e.target.value }))} />
              </FormRow>
              <SaveBtn onClick={save} disabled={!compleet} onInvalid={() => setAttentie(true)} />
            </div>
          </Section>
          <Section title={`Metingen vandaag (${today.length}${afwijkingenVandaag ? `, ${afwijkingenVandaag} afwijking${afwijkingenVandaag === 1 ? "" : "en"}` : ""})`}>
            {today.length === 0 ? (
              <Empty text="Nog geen metingen vandaag" />
            ) : (
              today.map((r) => (
                <RecordRow
                  key={r.id}
                  afwijking={r.afwijking}
                  left={<><b>{r.unit_naam || "?"}</b> &nbsp;·&nbsp; <span style={{ color: r.afwijking ? C.red : C.green, fontWeight: 700 }}>{r.temp}°C</span></>}
                  right={r.medewerker}
                  sub={`${fmtTime(r.created_at)}${r.afwijking ? " ⚠️ AFWIJKING" : ""}${temperatuurDetail(r)}${r.opmerking ? ` · ${r.opmerking}` : ""}`}
                  onDelete={() => { sbDelete("haccp_temp", r.id); setRecords((rs) => rs.filter((x) => x.id !== r.id)); }}
                />
              ))
            )}
          </Section>
          <Section title={toonMeerHistorie ? "Geschiedenis (laatste 50)" : "Geschiedenis (laatste 5)"}>
            {loading ? (
              <div style={{ color: C.txt3 }}>Laden…</div>
            ) : (
              <>
                {historieZichtbaar.map((r) => (
                <RecordRow
                  key={r.id}
                  afwijking={r.afwijking}
                  left={<><b>{r.unit_naam || "?"}</b> &nbsp;·&nbsp; <span style={{ color: r.afwijking ? C.red : C.green, fontWeight: 700 }}>{r.temp}°C</span></>}
                  right={r.medewerker}
                  sub={`${fmtDate(r.created_at)} ${fmtTime(r.created_at)}${temperatuurDetail(r)}${r.opmerking ? ` · ${r.opmerking}` : ""}`}
                  onDelete={() => { sbDelete("haccp_temp", r.id); setRecords((rs) => rs.filter((x) => x.id !== r.id)); }}
                />
                ))}
                {historie.length > 5 && (
                  <button onClick={() => setToonMeerHistorie((v) => !v)} style={{ marginTop: 8, minHeight: 38, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.bg2, color: C.primary, fontFamily: "inherit", fontWeight: 700, cursor: "pointer" }}>
                    {toonMeerHistorie ? "Minder tonen" : `Meer tonen (${Math.min(historie.length, 50) - 5})`}
                  </button>
                )}
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// ONTVANGST TAB
// ════════════════════════════════════════════════════════════════════════
function OntvangstTab({ cfg, updateCfg }) {
  const [records, setRecords, loading] = useRealtimeRecords("haccp_ontvangst", 100);
  const [form, setForm] = useState({ leverancier: "", product: "", temp: "", thermometer_id: cfg?.thermometers?.length === 1 ? cfg.thermometers[0].id : "", tht_ok: null, verpakking_ok: null, medewerker: "", opmerking: "" });
  const [attentie, setAttentie] = useState(false);
  const [toonMeerHistorie, setToonMeerHistorie] = useState(false);

  const save = async () => {
    if (!form.leverancier || !form.product || !form.medewerker || !form.temp || form.tht_ok === null || form.verpakking_ok === null) return;
    const tempGemeten = parseFloat(String(form.temp).replace(",", "."));
    const thermometer = thermometerVoor(cfg, form.thermometer_id);
    const gecorrigeerd = corrigeerTemperatuur(tempGemeten, thermometer);
    const tempNum = gecorrigeerd.waarde;
    const afwijking = !form.tht_ok || !form.verpakking_ok || (!isNaN(tempNum) && tempNum > 7);
    const naam = form.leverancier.trim();
    const bestaat = (cfg.leveranciers || []).some((l) => l.naam.toLowerCase() === naam.toLowerCase());
    if (naam && !bestaat && updateCfg) updateCfg((c) => ({ leveranciers: [...(c.leveranciers || []), { id: uid(), naam }] }));
    const rec = await sbPost("haccp_ontvangst", { ...form, leverancier: naam, temp: tempNum, temp_gemeten: Number.isFinite(tempGemeten) ? tempGemeten : null, temp_correctie: gecorrigeerd.correctie, thermometer_id: form.thermometer_id || null, afwijking, dag: todayDay, created_at: new Date().toISOString() });
    if (rec) setRecords((r) => [rec, ...r]);
    setForm({ leverancier: "", product: "", temp: "", thermometer_id: form.thermometer_id, tht_ok: null, verpakking_ok: null, medewerker: form.medewerker, opmerking: "" });
  };

  const thermometerVerplicht = (cfg.thermometers || []).length > 0;
  const mist = { leverancier: !form.leverancier, product: !form.product, temp: !form.temp, thermometer: thermometerVerplicht && !form.thermometer_id, tht: form.tht_ok === null, verpakking: form.verpakking_ok === null, medewerker: !form.medewerker };
  const compleet = !Object.values(mist).some(Boolean);
  const tempGemetenPreview = parseFloat(String(form.temp).replace(",", "."));
  const tempPreview = corrigeerTemperatuur(tempGemetenPreview, thermometerVoor(cfg, form.thermometer_id));
  const huidigeAfwijking = (!isNaN(tempPreview.waarde) && tempPreview.waarde > 7) || form.tht_ok === false || form.verpakking_ok === false;
  const vandaag = records.filter((r) => r.dag === todayDay);
  const afwijkingenVandaag = vandaag.filter((r) => r.afwijking).length;
  const historieZichtbaar = toonMeerHistorie ? records.slice(0, 50) : records.slice(0, 5);
  useEffect(() => { if (compleet && attentie) setAttentie(false); }, [compleet, attentie]);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <HaccpInlineBeheer soort="leveranciers" cfg={cfg} updateCfg={updateCfg} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <TempStatusCard label="Vandaag" value={`${vandaag.length} ontvangst${vandaag.length === 1 ? "" : "en"}`} tone={vandaag.length > 0 ? "ok" : "open"} />
        <TempStatusCard label="Afwijkingen" value={`${afwijkingenVandaag}`} tone={afwijkingenVandaag > 0 ? "bad" : "ok"} />
      </div>
      <Section title="Ontvangst registreren">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <FormRow label="Leverancier"><LeverancierSelect cfg={cfg} value={form.leverancier} onChange={(v) => setForm((f) => ({ ...f, leverancier: v }))} error={attentie && mist.leverancier} /></FormRow>
          <FormRow label="Product"><input style={{ ...inputStyle, ...(attentie && mist.product ? { borderColor: C.red, borderWidth: 2, background: C.redBg } : {}) }} placeholder="Productnaam" value={form.product} onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))} /></FormRow>
          <FormRow label="Temp °C"><TempVrijVeld value={form.temp} onChange={(v) => setForm((f) => ({ ...f, temp: v }))} grens={7} placeholder="bijv. 3.5" error={attentie && mist.temp} /></FormRow>
          {(cfg.thermometers || []).length > 0 && <FormRow label="Thermometer"><ThermometerSelect cfg={cfg} value={form.thermometer_id} onChange={(v) => setForm((f) => ({ ...f, thermometer_id: v }))} /></FormRow>}
          <FormRow label="THT/TGT OK"><ToggleBtn value={form.tht_ok} onChange={(v) => setForm((f) => ({ ...f, tht_ok: v }))} error={attentie && mist.tht} /></FormRow>
          <FormRow label="Verpakking OK"><ToggleBtn value={form.verpakking_ok} onChange={(v) => setForm((f) => ({ ...f, verpakking_ok: v }))} error={attentie && mist.verpakking} /></FormRow>
          <FormRow label="Medewerker"><MedewerkerSelect cfg={cfg} value={form.medewerker} onChange={(v) => setForm((f) => ({ ...f, medewerker: v }))} error={attentie && mist.medewerker} /></FormRow>
          <FormRow label="Opmerking"><input style={inputStyle} placeholder="Optioneel…" value={form.opmerking} onChange={(e) => setForm((f) => ({ ...f, opmerking: e.target.value }))} /></FormRow>
          {huidigeAfwijking && !form.opmerking && (
            <div style={{ padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.orangeBd}`, background: C.orangeBg, color: C.orange, fontSize: 12, fontWeight: 700 }}>
              Afwijking: noteer kort wat je hebt gecontroleerd of afgesproken. Dit komt mee in het NVWA rapport.
            </div>
          )}
          <SaveBtn onClick={save} disabled={!compleet} onInvalid={() => setAttentie(true)} />
        </div>
      </Section>
      <Section title={toonMeerHistorie ? "Registraties (laatste 50)" : "Registraties (laatste 5)"}>
        {loading ? <div style={{ color: C.txt3 }}>Laden…</div> : (
          <>
            {historieZichtbaar.map((r) => (
            <RecordRow key={r.id} afwijking={r.afwijking}
              left={<><b>{r.leverancier}</b> — {r.product}</>}
              right={r.medewerker}
              sub={`${fmtDate(r.created_at)} · ${r.temp != null ? `${r.temp}°C${temperatuurDetail(r)} · ` : ""}THT: ${r.tht_ok ? "✓" : "✗"} · Verpakking: ${r.verpakking_ok ? "✓" : "✗"}${r.opmerking ? ` · ${r.opmerking}` : ""}`}
              onDelete={() => { sbDelete("haccp_ontvangst", r.id); setRecords((rs) => rs.filter((x) => x.id !== r.id)); }}
            />
            ))}
            {records.length > 5 && (
              <button onClick={() => setToonMeerHistorie((v) => !v)} style={{ marginTop: 8, minHeight: 38, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.bg2, color: C.primary, fontFamily: "inherit", fontWeight: 700, cursor: "pointer" }}>
                {toonMeerHistorie ? "Minder tonen" : `Meer tonen (${Math.min(records.length, 50) - 5})`}
              </button>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TERUGKOELEN TAB
// ════════════════════════════════════════════════════════════════════════
function TerugkoelTab({ cfg, coolingRequest, onCoolingRequestConsumed }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [showStart, setShowStart] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [form, setForm] = useState({ product: "", temperature: "", medewerker: "", thermometer_id: cfg?.thermometers?.length === 1 ? cfg.thermometers[0].id : "", opmerking: "" });
  const [measurement, setMeasurement] = useState(null);
  const tenant = _tid;
  const api = _sb;
  const clock = (value) => new Date(value).toLocaleTimeString("nl-NL", { timeZone: "Europe/Amsterdam", hour: "2-digit", minute: "2-digit" });
  const date = (value) => new Date(value).toLocaleString("nl-NL", { timeZone: "Europe/Amsterdam" });
  const day = (value) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Amsterdam" }).format(new Date(value));

  async function load() {
    if (!api || !tenant) throw new Error("Geen verbinding met de keuken.");
    // All unfinished records, regardless of age; history has its own limit.
    const open = [];
    for (let offset = 0; ; offset += 500) {
      const rows = await api.selectStrict("haccp_terugkoel", "tenant_id=eq." + tenant + "&or=(eind_tijd.is.null,eind_tijd.eq.,eind_temp.is.null)&order=created_at.asc,id.asc&limit=500&offset=" + offset);
      if (!Array.isArray(rows)) throw new Error("Registraties konden niet worden geladen.");
      open.push(...rows);
      if (rows.length < 500) break;
    }
    const recent = await api.selectStrict("haccp_terugkoel", "tenant_id=eq." + tenant + "&order=created_at.desc&limit=100");
    if (!Array.isArray(recent)) throw new Error("Historie kon niet worden geladen.");
    return [...new Map([...recent, ...open].map(r => [r.id, r])).values()];
  }
  useEffect(() => {
    let alive = true;
    let inFlight = false;
    setRecords([]); setMeasurement(null); setShowStart(false); setLoading(true);
    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      try { const rows = await load(); if (alive) { setRecords(rows); setError(""); } }
      catch { if (alive) setError("Laden mislukt. Controleer je verbinding; controles blijven openstaan."); }
      finally { inFlight = false; if (alive) setLoading(false); }
    };
    refresh();
    const timer = window.setInterval(() => { setNow(Date.now()); refresh(); }, 15000);
    const focus = () => { setNow(Date.now()); refresh(); };
    window.addEventListener("focus", focus);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", focus); };
  }, [tenant, api]);

  useEffect(() => {
    if (!coolingRequest) return;
    setForm(f => ({ ...f, source_mep_item_id: coolingRequest.itemId || null, product: coolingRequest.product || "", temperature: "", opmerking: "" }));
    setError("");
    setShowStart(true);
    onCoolingRequestConsumed?.();
  }, [coolingRequest, onCoolingRequestConsumed]);

  async function save(action) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError("");
    try { await action(); setRecords(await load()); }
    catch (e) { setError(e.message || "Opslaan mislukt. Probeer opnieuw."); }
    finally { lock.current = false; setBusy(false); }
  }
  function corrected(raw, thermometerId) {
    const value = parseCoolingTemperature(raw);
    if (value === null) throw new Error("Vul een geldige temperatuur in (-50 tot 150 °C).");
    const result = corrigeerTemperatuur(value, thermometerVoor(cfg, thermometerId));
    return { measured: value, value: result.waarde, correction: result.correctie };
  }
  async function start() {
    await save(async () => {
      if (!form.product.trim() || !form.medewerker || ((cfg.thermometers || []).length && !form.thermometer_id)) throw new Error("Vul product, medewerker en thermometer in.");
      const t = corrected(form.temperature, form.thermometer_id);
      const timestamp = new Date().toISOString();
      const result = await api.insert("haccp_terugkoel", {
        tenant_id: tenant, source_mep_item_id: form.source_mep_item_id || null, product: form.product.trim(), medewerker: form.medewerker,
        thermometer_id: form.thermometer_id || null, opmerking: form.opmerking,
        started_at: timestamp, start_tijd: clock(timestamp), created_at: timestamp, dag: day(timestamp),
        start_temp: t.value, start_temp_gemeten: t.measured, start_temp_correctie: t.correction,
        eind_temp: null, eind_tijd: "", duur_min: null, afwijking: false
      });
      const row = Array.isArray(result) ? result[0] : result;
      if (!row?.id) throw new Error("Starten niet bevestigd. Controleer de lijst voordat je opnieuw start.");
      setShowStart(false); setForm(f => ({ ...f, source_mep_item_id: null, product: "", temperature: "", opmerking: "" }));
    });
  }
  async function recordMeasurement() {
    await save(async () => {
      const m = measurement;
      if (!m.medewerker) throw new Error("Kies de medewerker die meet.");
      const rows = await api.selectStrict("haccp_terugkoel", "tenant_id=eq." + tenant + "&id=eq." + m.record.id);
      const r = rows?.[0];
      if (!r || coolingStatus(r) === "closed") throw new Error("Deze registratie is al afgerond of niet meer beschikbaar.");
      if (m.kind === "interim" && r.interim_at) throw new Error("Een collega heeft de tussenmeting al opgeslagen. Vernieuw het overzicht.");
      const t = corrected(m.temperature, r.thermometer_id);
      const timestamp = new Date().toISOString();
      const final = m.kind === "final";
      const afwijking = final && coolingDeviation(r, t.value);
      if (afwijking && !m.note.trim()) throw new Error("Noteer de genomen maatregel of toelichting bij deze afwijking.");
      const patch = final ? {
        finished_at: timestamp, eind_tijd: clock(timestamp), eind_temp: t.value,
        eind_temp_gemeten: t.measured, eind_temp_correctie: t.correction,
        duur_min: coolingMinutes(r), afwijking, finished_by: m.medewerker,
        opmerking: [r.opmerking, m.note.trim()].filter(Boolean).join("; ")
      } : {
        interim_at: timestamp, interim_temp: t.value, interim_temp_gemeten: t.measured,
        interim_temp_correctie: t.correction, interim_by: m.medewerker, interim_note: m.note.trim()
      };
      // Conditional write avoids overwriting a colleague's completed measurement.
      const guard = final ? "&or=(eind_tijd.is.null,eind_tijd.eq.,eind_temp.is.null)" : "&interim_at=is.null&or=(eind_tijd.is.null,eind_tijd.eq.,eind_temp.is.null)";
      await api.update("haccp_terugkoel", "tenant_id=eq." + tenant + "&id=eq." + r.id + guard, patch);
      const check = await api.selectStrict("haccp_terugkoel", "tenant_id=eq." + tenant + "&id=eq." + r.id);
      if (Date.parse(check?.[0]?.[final ? "finished_at" : "interim_at"]) !== Date.parse(timestamp)) throw new Error("Opslaan niet bevestigd of een collega was eerder. Vernieuw het overzicht.");
      setMeasurement(null);
    });
  }
  const open = records.filter(r => coolingStatus(r, now) !== "closed");
  const closed = records.filter(r => coolingStatus(r, now) === "closed").sort((a,b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  const due = open.filter(r => ["interim_due", "final_due", "legacy"].includes(coolingStatus(r, now)));
  const buttonStyle = { minHeight: 44, borderRadius: 8, padding: "8px 16px", border: "1px solid " + C.bdr, background: C.bg2, color: C.txt, fontWeight: 700, cursor: "pointer" };
  const primary = { ...buttonStyle, background: C.primary, color: "#fff" };
  const field = (label, value, onChange, numeric = false) => <FormRow label={label}><input aria-label={label} style={{ ...inputStyle, minHeight: 44 }} type="text" inputMode={numeric ? "decimal" : "text"} value={value} onChange={e => onChange(e.target.value)} /></FormRow>;
  const openMeasurement = (r, kind) => { setError(""); setMeasurement({ record: r, kind, temperature: "", note: "", medewerker: "" }); };
  const modal = (title, content, action) => <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}><div role="dialog" aria-modal="true" aria-label={title} style={{ background: C.bg2, color: C.txt, borderRadius: 16, padding: 24, width: "min(520px, 100%)", maxHeight: "90dvh", overflowY: "auto", display: "grid", gap: 16 }}><h2 style={{ margin: 0 }}>{title}</h2>{content}{error && <p role="alert" style={{ color: C.red }}>{error}</p>}<div style={{ display: "flex", gap: 12 }}><button disabled={busy} style={buttonStyle} onClick={() => { setShowStart(false); setMeasurement(null); setError(""); }}>Annuleren</button>{action}</div></div></div>;

  return <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "grid", gap: 16 }}>
    <InfoBanner text="Binnen 5 uur naar maximaal 7 °C. Tussencontrole na 2 uur: richtwaarde maximaal 20 °C." />
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}><span>{open.length} lopend · {due.length} controles vragen aandacht</span><button style={primary} onClick={() => { setError(""); setForm(f => ({ ...f, source_mep_item_id: null, product: "", temperature: "" })); setShowStart(true); }}>Nieuwe terugkoeling</button></div>
    <p style={{ margin: 0, color: C.txt2 }}>Herinneringen zijn zichtbaar zolang dit HACCP-scherm openstaat. Je kunt eerder afronden zodra het product voldoende is afgekoeld.</p>
    {error && !showStart && !measurement && <p role="alert" style={{ color: C.red }}>{error}</p>}
    {loading && <p>Laden…</p>}
    <Section title="Lopende terugkoelingen">
      {!loading && !open.length && <Empty text="Geen lopende terugkoelingen" />}
      {[...open].sort((a,b) => Date.parse(a.created_at) - Date.parse(b.created_at)).map(r => {
        const status = coolingStatus(r, now);
        const label = { running: "Terugkoeling bezig", interim_due: "Tussenmeting nodig", final_due: "Eindmeting ontbreekt", legacy: "Oude registratie — startdatum niet vastgelegd" }[status];
        const attention = status !== "running";
        return <article key={r.id} style={{ background: status === "final_due" ? C.redBg : attention ? C.orangeBg : C.bg2, border: "1px solid " + C.bdr, borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <strong>{r.product}</strong><p role={attention ? "status" : undefined}>{label}{status === "running" ? " · " + duurTekst(coolingMinutes(r, now)) : ""}</p>
          <p>Start: {r.started_at ? date(r.started_at) : (r.dag || fmtDate(r.created_at)) + " " + (r.start_tijd || "")} · {r.start_temp} °C · {r.medewerker}</p>
          {r.started_at && <p>Controle uiterlijk: {date(Date.parse(r.started_at) + 5 * 3600000)}</p>}
          {r.interim_at && <p>Tussenmeting: {r.interim_temp} °C · {date(r.interim_at)} · {r.interim_by}{Number(r.interim_temp) > 20 && " — boven de richtwaarde; versnel de terugkoeling."}</p>}
          {status === "legacy" && <p>De oorspronkelijke volledige starttijd is onbekend. Afronden wordt als afwijking vastgelegd met toelichting.</p>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{!r.interim_at && status !== "final_due" && status !== "legacy" && <button style={buttonStyle} onClick={() => openMeasurement(r, "interim")}>Tussenmeting invoeren</button>}<button style={primary} onClick={() => openMeasurement(r, "final")}>Eindmeting invoeren</button></div>
        </article>;
      })}
    </Section>
    <Section title="Afgeronde registraties">
      {closed.slice(0, showHistory ? 100 : 5).map(r => <article key={r.id} style={{ borderBottom: "1px solid " + C.bdr, padding: 12 }}><strong>{r.product}</strong><p>{r.finished_at ? date(r.finished_at) : fmtDate(r.created_at)} · Eind: {r.eind_temp} °C · {r.duur_min != null ? duurTekst(r.duur_min) : "Duur onbekend"}{r.afwijking ? " · Afwijking" : ""}</p><p>{r.interim_at ? "Tussenmeting: " + r.interim_temp + " °C · " + date(r.interim_at) : "Tussenmeting niet vastgelegd"}</p>{r.interim_note && <p>{r.interim_note}</p>}{r.opmerking && <p>{r.opmerking}</p>}</article>)}
      {closed.length > 5 && <button style={buttonStyle} onClick={() => setShowHistory(!showHistory)}>{showHistory ? "Minder tonen" : "Meer tonen"}</button>}
    </Section>
    {showStart && modal("Terugkoeling starten", <>
      {field("Product", form.product, v => setForm(f => ({ ...f, product: v })))}
      {field("Starttemperatuur °C", form.temperature, v => setForm(f => ({ ...f, temperature: v })), true)}
      <p>Starttijd: nu. Start direct wanneer de bereiding klaar is; later klikken verschuift de voedselveiligheidstermijn niet.</p>
      {(cfg.thermometers || []).length > 0 && <FormRow label="Thermometer"><ThermometerSelect cfg={cfg} value={form.thermometer_id} onChange={v => setForm(f => ({ ...f, thermometer_id: v }))} /></FormRow>}
      <FormRow label="Medewerker"><MedewerkerSelect cfg={cfg} value={form.medewerker} onChange={v => setForm(f => ({ ...f, medewerker: v }))} /></FormRow>
      {field("Opmerking", form.opmerking, v => setForm(f => ({ ...f, opmerking: v })))}
      <p>Controle na 2 uur en eindmeting uiterlijk na 5 uur. Je sluit de registratie zelf af.</p>
    </>, <button disabled={busy} style={primary} onClick={start}>{busy ? "Opslaan…" : "Start terugkoeling"}</button>)}
    {measurement && modal(measurement.kind === "final" ? "Terugkoeling afronden" : "Tussenmeting", <>
      <strong>{measurement.record.product}</strong>
      {field("Gemeten temperatuur °C", measurement.temperature, v => setMeasurement(m => ({ ...m, temperature: v })), true)}
      <p>Meet nu met de thermometer. De huidige datum en tijd worden opgeslagen; deze meting wordt niet teruggedateerd.</p>
      <FormRow label="Medewerker"><MedewerkerSelect cfg={cfg} value={measurement.medewerker} onChange={v => setMeasurement(m => ({ ...m, medewerker: v }))} /></FormRow>
      {field("Maatregel of toelichting", measurement.note, v => setMeasurement(m => ({ ...m, note: v })))}
      {measurement.kind === "interim" && <p>Richtwaarde na 2 uur: maximaal 20 °C. Is het product al maximaal 7 °C? Kies Annuleren en leg een eindmeting vast.</p>}
    </>, <button disabled={busy} style={primary} onClick={recordMeasurement}>{busy ? "Opslaan…" : measurement.kind === "final" ? "Afronden en opslaan" : "Meting opslaan"}</button>)}
  </div>;
}


// ════════════════════════════════════════════════════════════════════════
// SCHOONMAAK TAB
// ════════════════════════════════════════════════════════════════════════
function SchoonmaakTab({ cfg, updateCfg }) {
  const [records, setRecords, loading] = useRealtimeRecords("haccp_schoonmaak", 200);
  const [activeFreq, setActiveFreq] = useState("dagelijks");
  const [signForm, setSignForm] = useState({ medewerker: "" });
  const [signAttentie, setSignAttentie] = useState(false);
  const [toonMeerHistorie, setToonMeerHistorie] = useState(false);
  const [recentAfgemeldId, setRecentAfgemeldId] = useState(null);
  const [busyTaakId, setBusyTaakId] = useState(null);

  const taken = cfg.schoonmaakTaken?.[activeFreq] || [];

  const isoWeek = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return {
      year: d.getUTCFullYear(),
      week: Math.ceil((((d - yearStart) / 86400000) + 1) / 7),
    };
  };

  const periodeKey = (freq, dateLike = new Date()) => {
    const d = new Date(dateLike);
    if (isNaN(d.getTime())) return null;
    if (freq === "dagelijks") {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }
    if (freq === "wekelijks") {
      const w = isoWeek(d);
      return `${w.year}-W${String(w.week).padStart(2, "0")}`;
    }
    if (freq === "vierweeks") {
      // Vaste blokken van exact 28 dagen, gestart op maandag 1 januari 2024.
      // Hierdoor is een taak steeds precies vier weken afgevinkt, ook rond jaarwisselingen.
      const dag = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const anker = new Date(2024, 0, 1);
      const dagen = Math.floor((dag - anker) / 86400000);
      return `P${Math.floor(dagen / 28)}`;
    }
    return null;
  };

  const recordHoortBijPeriode = (r, freq) => {
    if (!r?.created_at) return false;
    if (r.frequentie && r.frequentie !== freq) return false;
    return periodeKey(freq, r.created_at) === periodeKey(freq);
  };

  const huidigeRecordsVoorTaak = (taak_id, freq = activeFreq) =>
    records.filter((r) => String(r.taak_id) === String(taak_id) && recordHoortBijPeriode(r, freq));

  const isDone = (taak_id, freq = activeFreq) => huidigeRecordsVoorTaak(taak_id, freq).length > 0;

  const isOverdue = (taak_id) => {
    if (isDone(taak_id)) return false;
    const last = records
      .filter((r) => String(r.taak_id) === String(taak_id) && (!r.frequentie || r.frequentie === activeFreq))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    if (!last) return false;
    const diff = (new Date() - new Date(last.created_at)) / (1000 * 60 * 60 * 24);
    if (activeFreq === "dagelijks") return diff > 1;
    if (activeFreq === "wekelijks") return diff > 7;
    if (activeFreq === "vierweeks") return diff > 28;
    return false;
  };

  const signOff = async (taak) => {
    if (!signForm.medewerker) { setSignAttentie(true); return; }
    if (isDone(taak.id) || busyTaakId === taak.id) return;
    setBusyTaakId(taak.id);
    try {
      const rec = await sbPost("haccp_schoonmaak", {
        taak_id: taak.id,
        taak_naam: taak.naam,
        frequentie: activeFreq,
        medewerker: signForm.medewerker,
        dag: todayDay,
        created_at: new Date().toISOString(),
      });
      if (rec) {
        setRecords((r) => r.some((x) => x.id === rec.id) ? r : [rec, ...r]);
        setRecentAfgemeldId(taak.id);
        try { window.dispatchEvent(new CustomEvent("kc:haccp-schoonmaak-changed")); } catch {}
        window.setTimeout(() => setRecentAfgemeldId((id) => id === taak.id ? null : id), 2200);
      }
    } finally {
      setBusyTaakId(null);
    }
  };

  const undoTaak = async (taak) => {
    if (busyTaakId === taak.id) return;
    const huidig = huidigeRecordsVoorTaak(taak.id);
    if (!huidig.length) return;
    setBusyTaakId(taak.id);
    try {
      await Promise.all(huidig.map((r) => sbDelete("haccp_schoonmaak", r.id)));
      const ids = new Set(huidig.map((r) => r.id));
      setRecords((rs) => rs.filter((r) => !ids.has(r.id)));
      setRecentAfgemeldId(null);
      try { window.dispatchEvent(new CustomEvent("kc:haccp-schoonmaak-changed")); } catch {}
    } finally {
      setBusyTaakId(null);
    }
  };

  const freqs = [
    { id: "dagelijks", label: "Dagelijks" },
    { id: "wekelijks", label: "Wekelijks" },
    { id: "vierweeks", label: "4-wekelijks" },
  ];

  const taakStatus = (freq) => {
    const lijst = cfg.schoonmaakTaken?.[freq] || [];
    const klaar = lijst.filter((taak) => isDone(taak.id, freq)).length;
    return { totaal: lijst.length, klaar, open: Math.max(lijst.length - klaar, 0) };
  };
  const statusDag = taakStatus("dagelijks");
  const statusWeek = taakStatus("wekelijks");
  const statusVier = taakStatus("vierweeks");
  const activeStatus = taakStatus(activeFreq);
  const history = records.slice(0, toonMeerHistorie ? 30 : 5);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <HaccpInlineBeheer soort="taken" cfg={cfg} updateCfg={updateCfg} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        {[
          { label: "Vandaag", status: statusDag },
          { label: "Deze week", status: statusWeek },
          { label: "4-wekelijks", status: statusVier },
        ].map((blok) => {
          const pct = blok.status.totaal ? (blok.status.klaar / blok.status.totaal) * 100 : 0;
          return (
            <div key={blok.label} style={{ background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.txt }}>{blok.label}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: blok.status.open ? C.gold : C.green }}>{blok.status.klaar}/{blok.status.totaal}</div>
              </div>
              <div style={{ fontSize: 11, color: C.txt3, marginTop: 3 }}>{blok.status.open ? `${blok.status.open} open` : "Alles geregistreerd"}</div>
              <div style={{ height: 5, borderRadius: 999, background: C.bg3, overflow: "hidden", marginTop: 8 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: blok.status.open ? C.gold : C.green, borderRadius: 999 }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6 }}>
        <span style={{ fontSize: 12, color: C.txt2, flexShrink: 0 }}>Medewerker:</span>
        <MedewerkerSelect cfg={cfg} value={signForm.medewerker} onChange={(v) => { setSignForm((f) => ({ ...f, medewerker: v })); setSignAttentie(false); }} error={signAttentie && !signForm.medewerker} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {freqs.map((f) => (
          <button key={f.id} onClick={() => setActiveFreq(f.id)} style={{ flex: 1, padding: "7px 8px", fontSize: 11, fontFamily: "inherit", fontWeight: 600, cursor: "pointer", borderRadius: 4, border: `1px solid ${activeFreq === f.id ? C.gold : C.bdr}`, background: activeFreq === f.id ? C.goldBg : C.bg2, color: activeFreq === f.id ? C.gold : C.txt2 }}>
            {f.label}
          </button>
        ))}
      </div>
      {taken.length === 0 ? (
        <EmptyState icon="🧹" text={<span>Nog geen {activeFreq}e taken. Voeg ze toe via <b>Beheren</b> hierboven.</span>} />
      ) : (
        <Section title={`${activeFreq.charAt(0).toUpperCase() + activeFreq.slice(1)}e taken · ${activeStatus.klaar}/${activeStatus.totaal} geregistreerd`}>
          {taken.map((taak) => {
            const done = isDone(taak.id), overdue = isOverdue(taak.id);
            const currentRecord = huidigeRecordsVoorTaak(taak.id)
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            const lastRecord = records
              .filter((r) => String(r.taak_id) === String(taak.id) && (!r.frequentie || r.frequentie === activeFreq))
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            const busy = busyTaakId === taak.id;
            return (
              <div key={taak.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: done ? C.greenBg : overdue ? C.redBg : C.bg2, border: `1px solid ${done ? C.greenBd : overdue ? C.redBd : C.bdr}`, borderRadius: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{done ? "✅" : overdue ? "🔴" : "⬜"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: done ? C.txt2 : C.txt, textDecoration: done ? "line-through" : "none" }}>{taak.naam}</div>
                  {done && currentRecord && (
                    <div style={{ fontSize: 11, color: C.green, marginTop: 2, fontWeight: 700 }}>
                      ✓ Uitgevoerd {fmtDate(currentRecord.created_at)} om {fmtTime(currentRecord.created_at)} door {currentRecord.medewerker}
                    </div>
                  )}
                  {recentAfgemeldId === taak.id && done && <div style={{ fontSize: 11, color: C.green, marginTop: 2, fontWeight: 700 }}>Opgeslagen</div>}
                  {!done && lastRecord && <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>Laatste: {fmtDate(lastRecord.created_at)} door {lastRecord.medewerker}</div>}
                  {overdue && !done && <div style={{ fontSize: 11, color: C.red, marginTop: 2, fontWeight: 600 }}>⚠️ Termijn verstreken!</div>}
                  {!lastRecord && !done && <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>Nog niet uitgevoerd</div>}
                </div>
                {done ? (
                  <button onClick={() => undoTaak(taak)} disabled={busy} style={{ background: "transparent", border: `1px solid ${C.greenBd}`, color: C.green, padding: "6px 10px", fontSize: 11, fontFamily: "inherit", fontWeight: 700, cursor: busy ? "default" : "pointer", borderRadius: 4, flexShrink: 0, opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Bezig…" : "Ongedaan maken"}
                  </button>
                ) : (
                  <button onClick={() => signOff(taak)} disabled={busy} style={{ background: signForm.medewerker ? C.hdr : C.bg3, border: `1px solid ${signForm.medewerker ? C.hdr : C.bdr}`, color: signForm.medewerker ? "#fff" : C.txt3, padding: "6px 14px", fontSize: 11, fontFamily: "inherit", fontWeight: 600, cursor: busy ? "default" : "pointer", borderRadius: 4, flexShrink: 0, opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Bezig…" : signForm.medewerker ? "Afvinken" : "Kies medewerker"}
                  </button>
                )}
              </div>
            );
          })}
        </Section>
      )}
      <Section title={toonMeerHistorie ? "Geschiedenis (laatste 30)" : "Geschiedenis (laatste 5)"}>
        {loading ? <div style={{ color: C.txt3 }}>Laden…</div> : (
          <>
            {history.map((r) => (
              <RecordRow key={r.id} afwijking={false}
                left={<><b>{r.taak_naam}</b> — {r.frequentie}</>}
                right={r.medewerker}
                sub={fmtDate(r.created_at, { weekday: "long", day: "numeric", month: "long" })}
                onDelete={() => { sbDelete("haccp_schoonmaak", r.id); setRecords((rs) => rs.filter((x) => x.id !== r.id)); }}
              />
            ))}
            {records.length > 5 && (
              <button onClick={() => setToonMeerHistorie(v => !v)} style={{ width: "100%", marginTop: 6, background: C.bg2, border: `1px dashed ${C.bdr2}`, color: C.primary, borderRadius: 6, padding: "8px 10px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {toonMeerHistorie ? "Minder tonen" : `Meer tonen (${records.length - 5})`}
              </button>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// ALLERGENEN TAB
// ════════════════════════════════════════════════════════════════════════
function AllergenenTab({ cfg, updateCfg }) {
  const [editId, setEditId] = useState(null);
  const leegForm = { naam: "", receptIds: [], handmatig: [], notitie: "" };
  const [form, setForm] = useState(leegForm);
  const [adding, setAdding] = useState(false);
  const [recepten, setRecepten] = useState([]);
  const [receptZoek, setReceptZoek] = useState("");
  const gerechten = cfg.allergenenGerechten || [];

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!_sb || !_tid) return;
        const rows = await _sb.select("recepten_data", `tenant_id=eq.${_tid}&select=data&limit=1`);
        const lijst = rows?.[0]?.data?.recepten || [];
        if (alive) {
          setRecepten(lijst.filter((r) => !r.archived).sort((a, b) => (a.naam || "").localeCompare(b.naam || "")));
        }
      } catch {
        if (alive) setRecepten([]);
      }
    })();
    return () => { alive = false; };
  }, [_tid]);

  const uniek = (waarden) => [...new Set((waarden || []).filter(Boolean))];
  const normaliseerGerecht = (g) => ({
    ...g,
    receptIds: g.receptIds || [],
    handmatig: g.handmatig || g.allergenen || [],
    notitie: g.notitie || "",
  });
  const heeftRecept = (ids, id) => (ids || []).some((x) => String(x) === String(id));
  const receptAllergenen = (ids) => uniek((ids || []).flatMap((id) => recepten.find((r) => String(r.id) === String(id))?.allergenen || []));
  const totaalAllergenen = (data) => uniek([...receptAllergenen(data.receptIds), ...(data.handmatig || data.allergenen || [])]);
  const geselecteerdeRecepten = recepten.filter((r) => heeftRecept(form.receptIds, r.id));
  const zichtbareRecepten = recepten
    .filter((r) => !receptZoek.trim() || (r.naam || "").toLowerCase().includes(receptZoek.trim().toLowerCase()))
    .slice(0, 24);
  const formTotaal = totaalAllergenen(form);
  const glutenActief = formTotaal.includes("Gluten") || form.handmatig.some((a) => GLUTEN_SUB.includes(a));
  const notenActief = formTotaal.includes("Noten") || form.handmatig.some((a) => NOTEN_SUB.includes(a));

  const save = () => {
    if (!form.naam.trim()) return;
    const schoon = {
      ...form,
      naam: form.naam.trim(),
      receptIds: form.receptIds || [],
      handmatig: form.handmatig || [],
      allergenen: totaalAllergenen(form),
    };
    if (editId) {
      updateCfg((c) => ({ allergenenGerechten: (c.allergenenGerechten || []).map((g) => g.id === editId ? { ...g, ...schoon } : g) }));
      setEditId(null);
    } else {
      updateCfg((c) => ({ allergenenGerechten: [...(c.allergenenGerechten || []), { id: uid(), ...schoon }] }));
      setAdding(false);
    }
    setForm(leegForm);
    setReceptZoek("");
  };
  const cancel = () => { setAdding(false); setEditId(null); setForm(leegForm); setReceptZoek(""); };
  const del = (id) => { if (!confirm("Gerecht verwijderen?")) return; updateCfg((c) => ({ allergenenGerechten: (c.allergenenGerechten || []).filter((g) => g.id !== id) })); };
  const toggleAllergen = (a) => setForm((f) => {
    const aan = f.handmatig.includes(a);
    let handmatig = aan ? f.handmatig.filter((x) => x !== a) : [...f.handmatig, a];
    if (aan && a === "Gluten") handmatig = handmatig.filter((x) => !GLUTEN_SUB.includes(x));
    if (aan && a === "Noten") handmatig = handmatig.filter((x) => !NOTEN_SUB.includes(x));
    return { ...f, handmatig };
  });
  const toggleSubAllergen = (a, hoofd) => setForm((f) => {
    const basis = f.handmatig.includes(hoofd) ? f.handmatig : [...f.handmatig, hoofd];
    return { ...f, handmatig: basis.includes(a) ? basis.filter((x) => x !== a) : [...basis, a] };
  });
  const toggleRecept = (id) => setForm((f) => ({ ...f, receptIds: heeftRecept(f.receptIds, id) ? f.receptIds.filter((x) => String(x) !== String(id)) : [...f.receptIds, id] }));
  const startEdit = (g) => {
    const n = normaliseerGerecht(g);
    setEditId(g.id);
    setForm({ naam: n.naam || "", receptIds: n.receptIds, handmatig: n.handmatig, notitie: n.notitie });
    setAdding(false);
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <InfoBanner text="Maak kaartgerechten aan en koppel recepten als bouwstenen. Vul alleen ontbrekende allergenen handmatig aan." />
      {gerechten.length > 0 && (
        <Section title={`Gerechten (${gerechten.length})`}>
          {gerechten.map((g) => {
            const n = normaliseerGerecht(g);
            const gekoppeld = recepten.filter((r) => heeftRecept(n.receptIds, r.id));
            const totaal = totaalAllergenen(n).length ? totaalAllergenen(n) : (g.allergenen || []);
            return (
            <div key={g.id} style={{ padding: "10px 14px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>{g.naam}</div>
                  <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>
                    {n.receptIds.length} recept{n.receptIds.length === 1 ? "" : "en"} gekoppeld
                    {n.handmatig.length > 0 ? ` - ${n.handmatig.length} handmatig` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => startEdit(g)} style={smallBtn(C.gold, C.goldBg)}>Bewerk</button>
                  <button onClick={() => del(g.id)} style={smallBtn(C.red, C.redBg)}>🗑</button>
                </div>
              </div>
              {gekoppeld.length > 0 && (
                <div style={{ fontSize: 11, color: C.txt3, marginBottom: 6 }}>
                  via: {gekoppeld.map((r) => r.naam).join(", ")}
                </div>
              )}
              {totaal.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {totaal.map((a) => <span key={a} style={{ background: C.redBg, border: `1px solid ${C.redBd}`, color: C.red, fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>{a}</span>)}
                </div>
              ) : <span style={{ fontSize: 11, color: C.txt3 }}>Geen allergenen geregistreerd</span>}
              {n.notitie && <div style={{ fontSize: 11, color: C.txt3, marginTop: 6 }}>{n.notitie}</div>}
            </div>
          );})}
        </Section>
      )}
      {(adding || editId) && (
        <Section title={editId ? "Gerecht bewerken" : "Nieuw gerecht"}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <FormRow label="Naam gerecht"><input style={inputStyle} placeholder="Bijv. hamburger met friet" value={form.naam} onChange={(e) => setForm((f) => ({ ...f, naam: e.target.value }))} /></FormRow>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
              <div style={{ border: `1px solid ${C.bdr}`, background: C.bg, borderRadius: 6, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.txt2, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Recepten koppelen</div>
                <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Zoek recept..." value={receptZoek} onChange={(e) => setReceptZoek(e.target.value)} />
                {recepten.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.txt3, fontStyle: "italic" }}>Nog geen recepten gevonden.</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 6, maxHeight: 230, overflowY: "auto", paddingRight: 2 }}>
                    {zichtbareRecepten.map((r) => {
                      const actief = heeftRecept(form.receptIds, r.id);
                      return (
                        <button key={r.id} type="button" onClick={() => toggleRecept(r.id)} style={{ textAlign: "left", padding: "8px 10px", borderRadius: 6, border: `1px solid ${actief ? C.pri : C.bdr}`, background: actief ? C.priBg : C.bg2, color: C.txt, cursor: "pointer", fontFamily: "inherit" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{actief ? "Gekozen: " : ""}{r.naam}</div>
                          <div style={{ fontSize: 10, color: C.txt3, marginTop: 2 }}>{(r.allergenen || []).length} allergenen</div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {geselecteerdeRecepten.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: C.txt3 }}>
                    Gekoppeld: {geselecteerdeRecepten.map((r) => r.naam).join(", ")}
                  </div>
                )}
              </div>
              <div style={{ border: `1px solid ${C.bdr}`, background: C.bg, borderRadius: 6, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.txt2, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Handmatig aanvullen</div>
                <div style={{ fontSize: 11, color: C.txt3, marginBottom: 8 }}>Voor gerechten zonder recept of allergenen die niet uit gekoppelde recepten komen.</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ALLERGENEN.map((a) => (
                    <button key={a} type="button" onClick={() => toggleAllergen(a)} style={{ padding: "5px 10px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 12, border: `1px solid ${form.handmatig.includes(a) ? C.red : C.bdr}`, background: form.handmatig.includes(a) ? C.redBg : C.bg2, color: form.handmatig.includes(a) ? C.red : C.txt2, fontWeight: form.handmatig.includes(a) ? 700 : 500 }}>{a}</button>
                  ))}
                </div>
                {glutenActief && (
                  <div style={{ padding: "10px 12px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: C.txt2, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>Gluten - specificeer graansoort</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {GLUTEN_SUB.map((sub) => {
                        const actief = form.handmatig.includes(sub);
                        return (
                          <button key={sub} type="button" onClick={() => toggleSubAllergen(sub, "Gluten")} style={{ padding: "4px 10px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 10, border: `1px solid ${actief ? C.red : C.bdr2}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, fontWeight: actief ? 700 : 500 }}>{sub}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {notenActief && (
                  <div style={{ padding: "10px 12px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: C.txt2, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>Noten - specificeer soort</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {NOTEN_SUB.map((sub) => {
                        const actief = form.handmatig.includes(sub);
                        return (
                          <button key={sub} type="button" onClick={() => toggleSubAllergen(sub, "Noten")} style={{ padding: "4px 10px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 10, border: `1px solid ${actief ? C.red : C.bdr2}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, fontWeight: actief ? 700 : 500 }}>{sub}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <FormRow label="Notitie"><input style={inputStyle} placeholder="Optioneel, bijv. saus apart geserveerd" value={form.notitie} onChange={(e) => setForm((f) => ({ ...f, notitie: e.target.value }))} /></FormRow>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.txt2, marginBottom: 4 }}>Totaal voor dit gerecht:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {totaalAllergenen(form).length > 0 ? totaalAllergenen(form).map((a) => (
                <span key={a} style={{ background: C.redBg, border: `1px solid ${C.redBd}`, color: C.red, fontSize: 10, padding: "3px 8px", borderRadius: 10, fontWeight: 700 }}>{a}</span>
              )) : <span style={{ fontSize: 12, color: C.txt3 }}>Nog geen allergenen geselecteerd</span>}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button onClick={save} style={{ flex: 1, background: C.gold, border: "none", color: "#fff", padding: 9, fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 4 }}>💾 Opslaan</button>
              <button onClick={cancel} style={{ background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt2, padding: "9px 14px", fontFamily: "inherit", fontSize: 12, cursor: "pointer", borderRadius: 4 }}>Annuleren</button>
            </div>
          </div>
        </Section>
      )}
      {!adding && !editId && (
        <button onClick={() => setAdding(true)} style={{ background: "transparent", border: `1px dashed ${C.bdr2}`, color: C.txt2, padding: "9px 14px", fontFamily: "inherit", fontSize: 12, cursor: "pointer", borderRadius: 4, textAlign: "left" }}>
          + Gerecht toevoegen
        </button>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// AFWIJKINGEN TAB
// ════════════════════════════════════════════════════════════════════════
function AfwijkingTab({ cfg }) {
  const [tempRecords, , tempLoading] = useRealtimeRecords("haccp_temp", 100);
  const [ontvangstRecords, , ontvangstLoading] = useRealtimeRecords("haccp_ontvangst", 100);
  const [terugkoelRecords, , terugkoelLoading] = useRealtimeRecords("haccp_terugkoel", 100);
  const [manualRecords, setManualRecords, manualLoading] = useRealtimeRecords("haccp_afwijking", 100);
  const loading = tempLoading || ontvangstLoading || terugkoelLoading || manualLoading;
  const automatischeAfwijkingen = [
    ...tempRecords.filter((r) => r.afwijking).map((r) => ({
      id: `temp-${r.id}`,
      bron: "Temperatuur",
      titel: `${r.unit_naam || "Koelunit"} - ${r.temp}°C`,
      datum: r.created_at,
      medewerker: r.medewerker,
      maatregel: r.opmerking,
      detail: tempNormTekst(r.type),
    })),
    ...ontvangstRecords.filter((r) => r.afwijking).map((r) => {
      const redenen = [];
      if (r.temp > 7) redenen.push(`temp ${r.temp}°C`);
      if (r.tht_ok === false) redenen.push("THT/TGT niet OK");
      if (r.verpakking_ok === false) redenen.push("verpakking niet OK");
      return {
        id: `ontvangst-${r.id}`,
        bron: "Ontvangst",
        titel: `${r.leverancier || "Leverancier"} - ${r.product || "Product"}`,
        datum: r.created_at,
        medewerker: r.medewerker,
        maatregel: r.opmerking,
        detail: redenen.join(" · ") || "Afwijking geregistreerd",
      };
    }),
    ...terugkoelRecords.filter((r) => r.afwijking).map((r) => {
      const redenen = [];
      if (r.eind_temp > 7) redenen.push(`eind ${r.eind_temp}°C`);
      if (r.duur_min > TERUGKOEL_MAX_MIN) redenen.push(`te lang: ${duurTekst(r.duur_min)}`);
      return {
        id: `terugkoel-${r.id}`,
        bron: "Terugkoelen",
        titel: r.product || "Product",
        datum: r.created_at,
        medewerker: r.medewerker,
        maatregel: r.opmerking,
        detail: redenen.join(" · ") || "Afwijking geregistreerd",
      };
    }),
  ].sort((a, b) => new Date(b.datum || 0) - new Date(a.datum || 0));

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <InfoBanner text="Afwijkingen worden hier verzameld uit temperatuur, ontvangst en terugkoelen. Registreren doe je bij de handeling zelf." color={C.orangeBg} borderColor={C.orangeBd} textColor={C.orange} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <TempStatusCard label="Totaal" value={`${automatischeAfwijkingen.length + manualRecords.length}`} tone={(automatischeAfwijkingen.length + manualRecords.length) > 0 ? "bad" : "ok"} />
        <TempStatusCard label="Uit registraties" value={`${automatischeAfwijkingen.length}`} tone={automatischeAfwijkingen.length > 0 ? "bad" : "ok"} />
        <TempStatusCard label="Handmatig" value={`${manualRecords.length}`} tone={manualRecords.length > 0 ? "warn" : "ok"} />
      </div>
      <Section title={`Afwijkingen uit registraties (${automatischeAfwijkingen.length})`}>
        {loading ? <div style={{ color: C.txt3 }}>Laden…</div> : automatischeAfwijkingen.length === 0 ? <Empty text="Geen afwijkingen gevonden in de laatste registraties" /> : (
          automatischeAfwijkingen.map((r) => (
            <div key={r.id} style={{ padding: "10px 14px", background: C.redBg, border: `1px solid ${C.redBd}`, borderLeft: `3px solid ${C.red}`, borderRadius: 6, marginBottom: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: C.txt3 }}>{fmtDate(r.datum)} - {r.medewerker || "onbekend"}</span>
                <span style={{ fontSize: 10, background: C.redBg, border: `1px solid ${C.redBd}`, color: C.red, padding: "2px 8px", borderRadius: 10, fontWeight: 800 }}>{r.bron}</span>
              </div>
              <div style={{ fontSize: 13, color: C.txt, marginBottom: 4, fontWeight: 800 }}>{r.titel}</div>
              <div style={{ fontSize: 12, color: C.txt2 }}>{r.detail}</div>
              {r.maatregel ? (
                <div style={{ fontSize: 12, color: C.txt2, marginTop: 4 }}><b>Maatregel/toelichting:</b> {r.maatregel}</div>
              ) : (
                <div style={{ fontSize: 12, color: C.orange, marginTop: 4, fontWeight: 700 }}>Geen maatregel/toelichting ingevuld bij de registratie.</div>
              )}
            </div>
          ))
        )}
      </Section>
      {manualRecords.length > 0 && (
        <Section title={`Handmatig geregistreerd (${manualRecords.length})`}>
          {manualRecords.map((r) => (
            <div key={r.id} style={{ padding: "10px 14px", background: C.orangeBg, border: `1px solid ${C.orangeBd}`, borderLeft: `3px solid ${C.orange}`, borderRadius: 6, marginBottom: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: C.txt3 }}>{fmtDate(r.created_at)} - {r.medewerker}</span>
                <button onClick={() => { sbDelete("haccp_afwijking", r.id); setManualRecords((rs) => rs.filter((x) => x.id !== r.id)); }} style={{ background: "none", border: "none", color: C.txt3, cursor: "pointer", fontSize: 12 }}>x</button>
              </div>
              <div style={{ fontSize: 13, color: C.txt, marginBottom: 4 }}><b>Afwijking:</b> {r.omschrijving}</div>
              {r.corrigerende_maatregel && <div style={{ fontSize: 12, color: C.txt2 }}><b>Maatregel:</b> {r.corrigerende_maatregel}</div>}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// HYGIËNE TAB
// ════════════════════════════════════════════════════════════════════════
function HygieneTab({ cfg }) {
  const [records, setRecords, loading] = useRealtimeRecords("haccp_hygiene", 100);
  const [form, setForm] = useState({ medewerker: "", klacht: "", thuis_gebleven: false, opmerking: "" });

  const save = async () => {
    if (!form.medewerker || !form.klacht) return;
    const rec = await sbPost("haccp_hygiene", { ...form, dag: todayDay, created_at: new Date().toISOString() });
    if (rec) setRecords((r) => [rec, ...r]);
    setForm({ medewerker: "", klacht: "", thuis_gebleven: false, opmerking: "" });
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <InfoBanner text="Registreer ziekmeldingen van medewerkers — met name maag/darmklachten." color={C.orangeBg} borderColor={C.orangeBd} textColor={C.orange} />
      <Section title="Ziekmelding registreren">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <FormRow label="Medewerker"><MedewerkerSelect cfg={cfg} value={form.medewerker} onChange={(v) => setForm((f) => ({ ...f, medewerker: v }))} /></FormRow>
          <FormRow label="Klacht">
            <select style={inputStyle} value={form.klacht} onChange={(e) => setForm((f) => ({ ...f, klacht: e.target.value }))}>
              <option value="">Kies klacht…</option>
              <option>Maag/darmklachten</option>
              <option>Overgeven</option>
              <option>Diarree</option>
              <option>Geelzucht</option>
              <option>Huidinfectie</option>
              <option>Overige klacht</option>
            </select>
          </FormRow>
          <FormRow label="Thuis gebleven"><ToggleBtn value={form.thuis_gebleven} onChange={(v) => setForm((f) => ({ ...f, thuis_gebleven: v }))} /></FormRow>
          <FormRow label="Opmerking"><input style={inputStyle} placeholder="Optioneel…" value={form.opmerking} onChange={(e) => setForm((f) => ({ ...f, opmerking: e.target.value }))} /></FormRow>
          <SaveBtn onClick={save} disabled={!form.medewerker || !form.klacht} />
        </div>
      </Section>
      <Section title="Ziekmeldingen">
        {loading ? <div style={{ color: C.txt3 }}>Laden…</div> : records.length === 0 ? <Empty text="Geen ziekmeldingen geregistreerd" /> : (
          records.map((r) => (
            <RecordRow key={r.id} afwijking={true}
              left={<><b>{r.medewerker}</b> — {r.klacht}</>}
              right={r.thuis_gebleven ? "🏠 thuis" : "👨‍🍳 aanwezig"}
              sub={`${fmtDate(r.created_at)}${r.opmerking ? ` · ${r.opmerking}` : ""}`}
              onDelete={() => { sbDelete("haccp_hygiene", r.id); setRecords((rs) => rs.filter((x) => x.id !== r.id)); }}
            />
          ))
        )}
      </Section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// INSTELLINGEN TAB
// ════════════════════════════════════════════════════════════════════════
function InstellingenTab({ cfg, updateCfg }) {
  const [newUnit, setNewUnit] = useState({ naam: "", type: "koel" });
  const [newMed, setNewMed] = useState("");
  const [newLev, setNewLev] = useState("");
  const [newTaak, setNewTaak] = useState({ naam: "", freq: "dagelijks" });
  const [edit, setEdit] = useState(null); // { soort:'unit'|'lev'|'taak', id, freq?, naam, type? }
  const [beheerTab, setBeheerTab] = useState("units");
  const takenTotaal = Object.values(cfg.schoonmaakTaken || {}).reduce((sum, lijst) => sum + (lijst?.length || 0), 0);
  const beheerTabs = [
    { id: "units", titel: "Koel & vriesunits", subtitel: "Temperatuurregistratie", aantal: (cfg.koelunits || []).length },
    { id: "leveranciers", titel: "Leveranciers", subtitel: "Ontvangstcontrole", aantal: (cfg.leveranciers || []).length },
    { id: "schoonmaak", titel: "Schoonmaaktaken", subtitel: "Dagelijks en periodiek", aantal: takenTotaal },
  ];

  const addUnit = () => { if (!newUnit.naam.trim()) return; updateCfg((c) => ({ koelunits: [...(c.koelunits || []), { id: uid(), ...newUnit }] })); setNewUnit({ naam: "", type: "koel" }); };
  const delUnit = (id) => updateCfg((c) => ({ koelunits: c.koelunits.filter((u) => u.id !== id) }));
  const addMed = () => { if (!newMed.trim()) return; updateCfg((c) => ({ medewerkers: [...(c.medewerkers || []), { id: uid(), naam: newMed.trim() }] })); setNewMed(""); };
  const delMed = (id) => updateCfg((c) => ({ medewerkers: c.medewerkers.filter((m) => m.id !== id) }));
  const addLev = () => { const naam = newLev.trim(); if (!naam) return; if (!(cfg.leveranciers || []).some((l) => l.naam.toLowerCase() === naam.toLowerCase())) updateCfg((c) => ({ leveranciers: [...(c.leveranciers || []), { id: uid(), naam }] })); setNewLev(""); };
  const delLev = (id) => updateCfg((c) => ({ leveranciers: (c.leveranciers || []).filter((l) => l.id !== id) }));
  const addTaak = () => { if (!newTaak.naam.trim()) return; updateCfg((c) => { const taken = { ...(c.schoonmaakTaken || {}) }; taken[newTaak.freq] = [...(taken[newTaak.freq] || []), { id: uid(), naam: newTaak.naam.trim() }]; return { schoonmaakTaken: taken }; }); setNewTaak((t) => ({ ...t, naam: "" })); };
  const delTaak = (freq, id) => updateCfg((c) => { const taken = { ...(c.schoonmaakTaken || {}) }; taken[freq] = taken[freq].filter((t) => t.id !== id); return { schoonmaakTaken: taken }; });

  // Bewerken (hernoemen) van bestaande items
  const bewaarEdit = () => {
    if (!edit || !edit.naam.trim()) { setEdit(null); return; }
    const naam = edit.naam.trim();
    if (edit.soort === "unit") updateCfg((c) => ({ koelunits: (c.koelunits || []).map((u) => u.id === edit.id ? { ...u, naam, type: edit.type } : u) }));
    else if (edit.soort === "lev") updateCfg((c) => ({ leveranciers: (c.leveranciers || []).map((l) => l.id === edit.id ? { ...l, naam } : l) }));
    else if (edit.soort === "taak") updateCfg((c) => { const taken = { ...(c.schoonmaakTaken || {}) }; taken[edit.freq] = (taken[edit.freq] || []).map((t) => t.id === edit.id ? { ...t, naam } : t); return { schoonmaakTaken: taken }; });
    setEdit(null);
  };

  const lijstRij = (label, onDel, onEdit) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginBottom: 4 }}>
      {label}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {onEdit && <button onClick={onEdit} style={smallBtn(C.txt2, C.bg3)}>Bewerk</button>}
        <button onClick={onDel} style={smallBtn(C.red, C.redBg)}>🗑</button>
      </div>
    </div>
  );

  // Bewerk-rij: invoerveld voor de naam (+ optioneel extra veld zoals het unit-type)
  const bewerkRij = (extra) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: C.bg2, border: `1px solid ${C.gold}`, borderRadius: 6, marginBottom: 4 }}>
      <input autoFocus style={{ ...inputStyle, flex: 1 }} value={edit.naam}
        onChange={(e) => setEdit((s) => ({ ...s, naam: e.target.value }))}
        onKeyDown={(e) => { if (e.key === "Enter") bewaarEdit(); if (e.key === "Escape") setEdit(null); }} />
      {extra}
      <button onClick={bewaarEdit} style={smallBtn(C.green, C.greenBg)}>✓</button>
      <button onClick={() => setEdit(null)} style={smallBtn(C.txt2, C.bg3)}>✕</button>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 20 }}>
      <InfoBanner text="Beheer hier de basislijsten die HACCP gebruikt: units voor temperatuur, leveranciers voor ontvangst en taken voor schoonmaak." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
        {beheerTabs.map((tab) => {
          const actief = beheerTab === tab.id;
          return (
            <button key={tab.id} type="button" onClick={() => setBeheerTab(tab.id)} style={{ textAlign: "left", padding: "12px 14px", background: actief ? C.primBg : C.bg2, border: `1px solid ${actief ? C.primary : C.bdr}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", color: C.txt }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{tab.titel}</span>
                <span style={{ minWidth: 26, textAlign: "center", padding: "2px 8px", borderRadius: 999, background: actief ? C.primary : C.bg3, color: actief ? "#fff" : C.txt2, fontSize: 11, fontWeight: 800 }}>{tab.aantal}</span>
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: C.txt3 }}>{tab.subtitel}</div>
            </button>
          );
        })}
      </div>
      {beheerTab === "units" && (
      <Section title="🧊 Koel & vriesunits">
        {(cfg.koelunits || []).map((u) => (
          <div key={u.id}>
            {edit && edit.soort === "unit" && edit.id === u.id
              ? bewerkRij(
                  <select style={{ ...inputStyle, width: 120 }} value={edit.type} onChange={(e) => setEdit((s) => ({ ...s, type: e.target.value }))}>
                    <option value="koel">Koeling</option>
                    <option value="vriezer">Vriezer</option>
                  </select>
                )
              : lijstRij(
                  <div><span style={{ fontSize: 13, fontWeight: 600, color: C.txt }}>{u.naam}</span><span style={{ fontSize: 11, color: C.txt3, marginLeft: 8 }}>{u.type === "vriezer" ? "❄️ Vriezer (norm ≤-18°C)" : "🧊 Koeling (norm ≤7°C)"}</span></div>,
                  () => delUnit(u.id),
                  () => setEdit({ soort: "unit", id: u.id, naam: u.naam, type: u.type })
                )}
          </div>
        ))}
        <div style={{ marginTop: 16, padding: 14, background: C.primBg, border: `1px solid ${C.primBd}`, borderLeft: `4px solid ${C.primary}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.primary, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Unit toevoegen</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) minmax(160px, 240px) auto", gap: 8, alignItems: "end" }}>
            <div>
              <div style={{ fontSize: 11, color: C.txt3, marginBottom: 5 }}>Naam unit</div>
              <input style={{ ...inputStyle, width: "100%" }} placeholder="Bijv. Vlees koeling" value={newUnit.naam} onChange={(e) => setNewUnit((f) => ({ ...f, naam: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addUnit()} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.txt3, marginBottom: 5 }}>Type</div>
              <select style={{ ...inputStyle, width: "100%" }} value={newUnit.type} onChange={(e) => setNewUnit((f) => ({ ...f, type: e.target.value }))}>
                <option value="koel">Koeling</option>
                <option value="vriezer">Vriezer</option>
              </select>
            </div>
            <button onClick={addUnit} style={{ minHeight: 34, background: C.gold, border: "none", color: "#fff", padding: "8px 20px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 4, flexShrink: 0 }}>Toevoegen</button>
          </div>
        </div>
      </Section>
      )}
      {beheerTab === "leveranciers" && (

      <Section title="🚚 Leveranciers">
        {(cfg.leveranciers || []).map((l) => (
          <div key={l.id}>
            {edit && edit.soort === "lev" && edit.id === l.id
              ? bewerkRij(null)
              : lijstRij(<span style={{ fontSize: 13, color: C.txt }}>{l.naam}</span>, () => delLev(l.id), () => setEdit({ soort: "lev", id: l.id, naam: l.naam }))}
          </div>
        ))}
        <div style={{ marginTop: 16, padding: 14, background: C.primBg, border: `1px solid ${C.primBd}`, borderLeft: `4px solid ${C.primary}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.primary, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Leverancier toevoegen</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) auto", gap: 8, alignItems: "end" }}>
            <div>
              <div style={{ fontSize: 11, color: C.txt3, marginBottom: 5 }}>Naam leverancier</div>
              <input style={{ ...inputStyle, width: "100%" }} placeholder="Bijv. Sligro" value={newLev} onChange={(e) => setNewLev(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLev()} />
            </div>
            <button onClick={addLev} style={{ minHeight: 34, background: C.gold, border: "none", color: "#fff", padding: "8px 20px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 4 }}>Toevoegen</button>
          </div>
        </div>
      </Section>
      )}
      {beheerTab === "schoonmaak" && (

      <Section title="🧹 Schoonmaaktaken">
        {["dagelijks", "wekelijks", "vierweeks"].map((freq) => (
          <div key={freq} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              {freq === "vierweeks" ? "4-wekelijks" : freq.charAt(0).toUpperCase() + freq.slice(1)}
            </div>
            {(cfg.schoonmaakTaken?.[freq] || []).map((t) => (
              <div key={t.id}>
                {edit && edit.soort === "taak" && edit.freq === freq && edit.id === t.id
                  ? bewerkRij(null)
                  : lijstRij(<span style={{ fontSize: 12, color: C.txt }}>{t.naam}</span>, () => delTaak(freq, t.id), () => setEdit({ soort: "taak", freq, id: t.id, naam: t.naam }))}
              </div>
            ))}
          </div>
        ))}
        <div style={{ marginTop: 16, padding: 14, background: C.primBg, border: `1px solid ${C.primBd}`, borderLeft: `4px solid ${C.primary}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.primary, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Taak toevoegen</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) minmax(160px, 240px) auto", gap: 8, alignItems: "end" }}>
            <div>
              <div style={{ fontSize: 11, color: C.txt3, marginBottom: 5 }}>Naam taak</div>
              <input style={{ ...inputStyle, width: "100%" }} placeholder="Bijv. Vloer keuken vegen" value={newTaak.naam} onChange={(e) => setNewTaak((f) => ({ ...f, naam: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addTaak()} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.txt3, marginBottom: 5 }}>Frequentie</div>
              <select style={{ ...inputStyle, width: "100%" }} value={newTaak.freq} onChange={(e) => setNewTaak((f) => ({ ...f, freq: e.target.value }))}>
                <option value="dagelijks">Dagelijks</option>
                <option value="wekelijks">Wekelijks</option>
                <option value="vierweeks">4-wekelijks</option>
              </select>
            </div>
            <button onClick={addTaak} style={{ minHeight: 34, background: C.gold, border: "none", color: "#fff", padding: "8px 20px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 4, flexShrink: 0 }}>Toevoegen</button>
          </div>
        </div>
      </Section>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// GEDEELDE HELPERS
// ════════════════════════════════════════════════════════════════════════
const inputStyle = {
  background: "#FFFFFF",
  border: `1px solid ${C.bdr}`,
  color: "#0F172A",
  padding: "7px 10px",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 12,
  outline: "none",
  borderRadius: 4,
  width: "100%",
  boxSizing: "border-box",
  WebkitTextFillColor: "#0F172A",
};
const smallBtn = (color, bg) => ({
  background: bg,
  border: `1px solid ${color}22`,
  color,
  padding: "4px 8px",
  fontSize: 11,
  fontFamily: "inherit",
  cursor: "pointer",
  borderRadius: 4,
});

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
function FormRow({ label, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", alignItems: "start", gap: 8 }}>
      <span style={{ fontSize: 12, color: C.txt2, fontWeight: 500, paddingTop: 9 }}>{label}</span>
      {children}
    </div>
  );
}
function SaveBtn({ onClick, disabled, onInvalid, label = "\uD83D\uDCBE Opslaan" }) {
  return (
    <button
      onClick={() => { if (disabled) { onInvalid && onInvalid(); } else onClick(); }}
      style={{ background: disabled ? C.bdr : C.primary, border: "none", color: disabled ? C.txt3 : "#fff", padding: "9px 14px", fontFamily: "DM Sans, inherit", fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", borderRadius: 4, marginTop: 4 }}
    >
      {label}
    </button>
  );
}
function MedewerkerSelect({ cfg, value, onChange, error }) {
  // De medewerkers komen binnen via cfg (op hoofdniveau gevuld uit team_data,
  // met terugval op de oude haccp_config-bron). Bewust NIET meer zelf uit
  // localStorage lezen: die is pas gevuld nadat de Team-module geopend is,
  // waardoor de lijst na een refresh leeg bleef.
  const lijst = cfg?.medewerkers || [];
  if (lijst.length === 0) {
    return <div style={{ fontSize: 12, color: C.txt3 }}>Nog geen medewerkers ingesteld (zie Team).</div>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, ...(error ? { padding: 8, borderRadius: 10, border: `2px solid ${C.red}`, background: C.redBg } : {}) }}>
      {lijst.map((m) => {
        const gekozen = value === m.naam;
        return (
          <button key={m.id} type="button"
            onClick={() => onChange(gekozen ? "" : m.naam)}
            style={{ minHeight: 44, padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: gekozen ? 700 : 500, border: gekozen ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`, background: gekozen ? C.primBg : C.bg2, color: gekozen ? C.primary : C.txt, WebkitTapHighlightColor: "transparent" }}>
            {m.naam}
          </button>
        );
      })}
    </div>
  );
}
function LeverancierSelect({ cfg, value, onChange, error }) {
  const lijst = cfg.leveranciers || [];
  const [nieuw, setNieuw] = useState(false);
  const errStyle = error ? { borderColor: C.red, borderWidth: 2, background: C.redBg } : {};
  if (nieuw || lijst.length === 0)
    return (
      <div style={{ display: "flex", gap: 6 }}>
        <input style={{ ...inputStyle, flex: 1, ...errStyle }} placeholder="Naam nieuwe leverancier" value={value} onChange={(e) => onChange(e.target.value)} autoFocus={nieuw} />
        {lijst.length > 0 && <button onClick={() => { setNieuw(false); onChange(""); }} style={{ ...inputStyle, width: "auto", padding: "0 12px", cursor: "pointer", background: C.bg3, color: C.txt2, fontWeight: 600, flexShrink: 0 }} title="Terug naar lijst">↩</button>}
      </div>
    );
  return (
    <select style={{ ...inputStyle, ...errStyle }} value={value} onChange={(e) => { if (e.target.value === "__nieuw__") { setNieuw(true); onChange(""); } else onChange(e.target.value); }}>
      <option value="">Kies leverancier…</option>
      {lijst.map((l) => <option key={l.id} value={l.naam}>{l.naam}</option>)}
      <option value="__nieuw__">+ Nieuwe leverancier…</option>
    </select>
  );
}
function tempIsAfwijking(type, t) {
  if (t === null || t === undefined || t === "" || isNaN(t)) return false;
  return type === "vriezer" ? parseFloat(t) > -18 : parseFloat(t) > 7;
}
function tempNormTekst(type) {
  return type === "vriezer" ? "Norm: <=-18°C" : "Norm: <=7°C";
}
function tempKleur(type, t, C) {
  return tempIsAfwijking(type, t) ? { bg: C.redBg, bd: C.redBd, fg: C.red } : { bg: C.greenBg, bd: C.greenBd, fg: C.green };
}
function TempStepper({ type, value, onChange, displayValue = null, statusValue = null, subtext = "" }) {
  const start = type === "vriezer" ? -18 : 4;
  const raw = value === "" || value === null || value === undefined ? start : parseFloat(String(value).replace(",", "."));
  const huidig = isNaN(raw) ? start : Math.round(raw * 2) / 2;
  const statusNum = Number.isFinite(Number(statusValue)) ? Number(statusValue) : huidig;
  const displayNum = Number.isFinite(Number(displayValue)) ? Number(displayValue) : huidig;
  const toon = Number.isInteger(displayNum) ? String(displayNum) : displayNum.toFixed(1);
  const k = tempKleur(type, statusNum, C);
  const tik = (d) => onChange(String(Math.round((huidig + d) * 2) / 2));
  const knop = (label, d) => (
    <button onClick={() => tik(d)} onTouchEnd={(e) => { e.preventDefault(); tik(d); }} style={{ width: 60, height: 60, flexShrink: 0, borderRadius: 12, border: `1px solid ${C.primBd}`, background: C.primBg, color: C.primary, fontSize: 28, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent", userSelect: "none" }}>{label}</button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
      {knop("−", -0.5)}
      <div style={{ flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 10, background: k.bg, border: `1px solid ${k.bd}`, color: k.fg }}>
        <span style={{ fontSize: 32, fontWeight: 800 }}>{toon}</span>
        <span style={{ fontSize: 16, marginLeft: 2 }}>°C</span>
        <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2 }}>{tempIsAfwijking(type, statusNum) ? "✗ afwijking" : "✓ binnen norm"}</div>
        {subtext ? <div style={{ fontSize: 10.5, marginTop: 3, opacity: 0.78 }}>{subtext}</div> : null}
      </div>
      {knop("+", 0.5)}
    </div>
  );
}
function TempVrijVeld({ value, onChange, grens = 7, placeholder = "bijv. 4.5", error }) {
  const heeft = value !== "" && value !== null && value !== undefined;
  const num = heeft ? parseFloat(String(value).replace(",", ".")) : null;
  const afw = num !== null && !isNaN(num) && num > grens;
  const k = error && !heeft ? { bg: C.redBg, bd: C.red, fg: C.red } : !heeft ? { bg: C.bg2, bd: C.bdr, fg: C.txt } : afw ? { bg: C.redBg, bd: C.redBd, fg: C.red } : { bg: C.greenBg, bd: C.greenBd, fg: C.green };
  return (
    <input type="number" step="0.1" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, background: k.bg, borderColor: k.bd, borderWidth: error && !heeft ? 2 : 1, color: k.fg, fontWeight: heeft ? 700 : 400 }} />
  );
}
function ToggleBtn({ value, onChange, error }) {
  const next = value === null ? true : value === true ? false : true;
  const isNull = value === null || value === undefined;
  const attentieRand = error && isNull;
  return (
    <button
      onClick={() => onChange(next)}
      onTouchEnd={(e) => { e.preventDefault(); onChange(next); }}
      style={{ padding: "6px 16px", fontSize: 12, fontFamily: "inherit", fontWeight: 700, cursor: "pointer", borderRadius: 4, border: attentieRand ? `2px solid ${C.red}` : `1px solid ${isNull ? C.bdr2 : value ? C.greenBd : C.redBd}`, background: attentieRand ? C.redBg : isNull ? C.bg3 : value ? C.greenBg : C.redBg, color: attentieRand ? C.red : isNull ? C.txt3 : value ? C.green : C.red, WebkitTapHighlightColor: "transparent" }}
    >
      {isNull ? "Ja / Nee?" : value ? "✓ Ja" : "✗ Nee"}
    </button>
  );
}
function RecordRow({ left, right, sub, afwijking, onDelete }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gridTemplateRows: "auto auto", padding: "9px 12px", background: afwijking ? C.redBg : C.bg2, border: `1px solid ${afwijking ? C.redBd : C.bdr}`, borderLeft: `3px solid ${afwijking ? C.red : C.greenBd}`, borderRadius: 6, marginBottom: 3, gap: "2px 10px" }}>
      <span style={{ fontSize: 13, color: C.txt }}>{left}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, color: C.txt2 }}>{right}</span>
        <button onClick={onDelete} style={{ background: "none", border: "none", color: C.txt3, cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
      </div>
      <span style={{ fontSize: 11, color: C.txt3, gridColumn: "1/3" }}>{sub}</span>
    </div>
  );
}
function InfoBanner({ text, color = "rgba(37,99,235,.08)", borderColor = "rgba(37,99,235,.25)", textColor = C.txt2 }) {
  return (
    <div style={{ padding: "10px 14px", background: color, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 12, color: textColor }}>
      ℹ️ {text}
    </div>
  );
}
function EmptyState({ icon, text }) {
  return (
    <div style={{ padding: "50px 20px", textAlign: "center", color: C.txt3 }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <p style={{ fontSize: 13 }}>{text}</p>
    </div>
  );
}
function Empty({ text }) {
  return <div style={{ padding: "16px 0", fontSize: 12, color: C.txt3, fontStyle: "italic" }}>{text}</div>;
}
function InlineLink({ children, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ background: "none", border: "none", padding: 0, margin: 0, color: C.primary, font: "inherit", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>
      {children}
    </button>
  );
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
  .kc-haccp, .kc-haccp * { font-family: 'DM Sans', system-ui, sans-serif; box-sizing: border-box; }
  .kc-haccp input, .kc-haccp textarea, .kc-haccp select { background:#FFFFFF; color:#0F172A; -webkit-text-fill-color:#0F172A; }
  .kc-haccp input:focus, .kc-haccp textarea:focus, .kc-haccp select:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,0.15); }
  .kc-haccp ::-webkit-scrollbar { width:5px; height:5px; }
  .kc-haccp ::-webkit-scrollbar-track { background:#F8FAFC; }
  .kc-haccp ::-webkit-scrollbar-thumb { background:#E2E8F0; border-radius:3px; }
`;

// =============================================================================
// NVWA RAPPORT MODAL
// =============================================================================
function RapportModal({ sb, tenantId, cfg, onClose }) {
  const now = new Date();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (now.getDay() === 0 ? 6 : now.getDay() - 1));
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = d => d.toISOString().slice(0, 10);

  const [van, setVan] = useState(fmt(mon));
  const [tot, setTot] = useState(fmt(sun));
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState("");

  const genereer = async () => {
    if (!van || !tot) { setFout("Kies een van- en tot-datum."); return; }
    if (van > tot) { setFout("Van-datum moet voor tot-datum liggen."); return; }
    setFout("");
    setLaden(true);

    try {
      const vanISO = `${van}T00:00:00.000Z`;
      const totISO = `${tot}T23:59:59.999Z`;
      const tid = tenantId ? `tenant_id=eq.${tenantId}&` : "";

      const [temp, ontvangst, terugkoel, schoonmaak, afwijking, hygiene] = await Promise.all([
        sb.select("haccp_temp",       `${tid}created_at=gte.${vanISO}&created_at=lte.${totISO}&order=created_at.asc&limit=500`),
        sb.select("haccp_ontvangst",  `${tid}created_at=gte.${vanISO}&created_at=lte.${totISO}&order=created_at.asc&limit=500`),
        sb.select("haccp_terugkoel",  `${tid}created_at=gte.${vanISO}&created_at=lte.${totISO}&order=created_at.asc&limit=500`),
        sb.select("haccp_schoonmaak", `${tid}created_at=gte.${vanISO}&created_at=lte.${totISO}&order=created_at.asc&limit=500`),
        sb.select("haccp_afwijking",  `${tid}created_at=gte.${vanISO}&created_at=lte.${totISO}&order=created_at.asc&limit=500`),
        sb.select("haccp_hygiene",    `${tid}created_at=gte.${vanISO}&created_at=lte.${totISO}&order=created_at.asc&limit=500`),
      ]);

      const appCfg = await sb.select("app_config", `tenant_id=eq.${tenantId}&select=bedrijfsnaam&limit=1`);
      const bedrijf = appCfg?.[0]?.bedrijfsnaam || cfg?.bedrijfsnaam || "KitchenControl";
      const datumBereik = `${van} t/m ${tot}`;

      const fmtD = s => { if (!s) return ""; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString("nl-NL"); };
      const fmtT = s => { if (!s) return ""; const d = new Date(s); return isNaN(d) ? s : d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }); };
      const fmtDT = s => { if (!s) return ""; return `${fmtD(s)} ${fmtT(s)}`; };
      const afw = v => v ? '<span class="afw">⚠ AFWIJKING</span>' : '<span class="ok">✓</span>';

      // sectie(titel, headers, htmlRijen) — htmlRijen is array van <td>...</td> strings
      const sectie = (titel, headers, htmlRijen) => {
        if (!htmlRijen?.length) return "";
        return `
          <div class="sectie">
            <h2>${titel} <span class="count">(${htmlRijen.length})</span></h2>
            <table>
              <thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead>
              <tbody>${htmlRijen.map(r => `<tr>${r}</tr>`).join("")}</tbody>
            </table>
          </div>`;
      };

      const tempRijen = (temp || []).map(r => `
        <td>${fmtD(r.created_at)}</td>
        <td>${fmtT(r.created_at)}</td>
        <td>${r.unit_naam || ""}</td>
        <td>${r.unit_type || ""}</td>
        <td><strong>${r.temp}°C</strong></td>
        <td>${r.unit_type === "vriezer" ? "≤-18°C" : "≤7°C"}</td>
        <td>${r.medewerker || ""}</td>
        <td>${r.opmerking || ""}</td>
        <td>${afw(r.afwijking)}</td>
      `);

      const ontvangstRijen = (ontvangst || []).map(r => `
        <td>${fmtD(r.created_at)}</td>
        <td>${r.leverancier || ""}</td>
        <td>${r.product || ""}</td>
        <td>${r.temp != null ? r.temp + "°C" : ""}</td>
        <td>${r.tht_ok ? "✓" : "✗"}</td>
        <td>${r.verpakking_ok ? "✓" : "✗"}</td>
        <td>${r.medewerker || ""}</td>
        <td>${r.opmerking || ""}</td>
        <td>${afw(r.afwijking)}</td>
      `);

      const terugkoelRijen = (terugkoel || []).map(r => {
        const dm = r.duur_min;
        const duur = dm != null ? `${Math.floor(dm/60)}u${String(dm%60).padStart(2,"0")}` : "";
        return `
          <td>${fmtD(r.created_at)}</td>
          <td>${r.product || ""}</td>
          <td>${r.start_tijd || ""}</td>
          <td>${r.eind_tijd || ""}</td>
          <td>${duur}</td>
          <td><strong>${r.eind_temp}°C</strong></td>
          <td>${r.medewerker || ""}</td>
          <td>${r.opmerking || ""}</td>
          <td>${afw(r.afwijking)}</td>
        `;
      });

      const schoonmaakRijen = (schoonmaak || []).map(r => `
        <td>${fmtD(r.created_at)}</td>
        <td>${r.taak_naam || ""}</td>
        <td>${r.frequentie || ""}</td>
        <td>${r.medewerker || ""}</td>
      `);

      const afwijkingRijen = [
        ...(temp || []).filter(r => r.afwijking).map(r => `
          <td>${fmtD(r.created_at)}</td>
          <td>Temperatuur</td>
          <td>${r.unit_naam || ""} - ${r.temp}°C</td>
          <td>${r.opmerking || ""}</td>
          <td>${r.medewerker || ""}</td>
        `),
        ...(ontvangst || []).filter(r => r.afwijking).map(r => {
          const redenen = [];
          if (r.temp > 7) redenen.push(`temp ${r.temp}°C`);
          if (r.tht_ok === false) redenen.push("THT/TGT niet OK");
          if (r.verpakking_ok === false) redenen.push("verpakking niet OK");
          return `
            <td>${fmtD(r.created_at)}</td>
            <td>Ontvangst</td>
            <td>${r.leverancier || ""} - ${r.product || ""}${redenen.length ? ` (${redenen.join(", ")})` : ""}</td>
            <td>${r.opmerking || ""}</td>
            <td>${r.medewerker || ""}</td>
          `;
        }),
        ...(terugkoel || []).filter(r => r.afwijking).map(r => {
          const redenen = [];
          if (r.eind_temp > 7) redenen.push(`eind ${r.eind_temp}°C`);
          if (r.duur_min > TERUGKOEL_MAX_MIN) redenen.push(`te lang ${duurTekst(r.duur_min)}`);
          return `
            <td>${fmtD(r.created_at)}</td>
            <td>Terugkoelen</td>
            <td>${r.product || ""}${redenen.length ? ` (${redenen.join(", ")})` : ""}</td>
            <td>${r.opmerking || ""}</td>
            <td>${r.medewerker || ""}</td>
          `;
        }),
        ...(afwijking || []).map(r => `
          <td>${fmtD(r.created_at)}</td>
          <td>Handmatig</td>
          <td>${r.omschrijving || ""}</td>
          <td>${r.corrigerende_maatregel || ""}</td>
          <td>${r.medewerker || ""}</td>
        `),
      ];

      const hygieneRijen = (hygiene || []).map(r => `
        <td>${fmtD(r.created_at)}</td>
        <td>${r.medewerker || ""}</td>
        <td>${r.klacht || ""}</td>
        <td>${r.thuis_gebleven ? "Ja" : "Nee"}</td>
        <td>${r.opmerking || ""}</td>
      `);

      const html = `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="utf-8">
  <title>HACCP Rapport – ${datumBereik}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, sans-serif; font-size: 11pt; color: #0F172A; padding: 15mm 15mm 20mm; }
    h1 { font-size: 18pt; font-weight: 800; margin-bottom: 4px; }
    .meta { font-size: 10pt; color: #475569; margin-bottom: 6px; }
    .periode { font-size: 10pt; color: #2563EB; font-weight: 600; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #0F172A; }
    .sectie { margin-bottom: 24px; page-break-inside: avoid; }
    h2 { font-size: 12pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #CBD5E1; color: #334155; }
    .count { font-size: 9pt; font-weight: 400; color: #94A3B8; }
    table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
    th { background: #F1F5F9; padding: 5px 8px; text-align: left; font-weight: 700; border: 1px solid #E2E8F0; font-size: 9pt; }
    td { padding: 4px 8px; border: 1px solid #E2E8F0; vertical-align: top; }
    tr:nth-child(even) td { background: #F8FAFC; }
    .afw { color: #DC2626; font-weight: 700; }
    .ok { color: #16A34A; }
    .leeg { font-size: 10pt; color: #94A3B8; font-style: italic; padding: 8px 0; }
    .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #CBD5E1; font-size: 9pt; color: #94A3B8; display: flex; justify-content: space-between; }
    .print-hint { background: #EFF6FF; border: 1px solid #BFDBFE; color: #1D4ED8; padding: 10px 14px; border-radius: 6px; font-size: 10pt; margin-bottom: 16px; }
    @media print { .print-hint { display: none !important; } }
    @page { margin: 15mm; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="print-hint">
    💡 Kies <strong>"Opslaan als PDF"</strong> als printer om het rapport op te slaan.
    Zet indien nodig <strong>"Achtergrondafbeeldingen"</strong> aan voor de opmaak.
  </div>
  <h1>HACCP Registratierapport</h1>
  <div class="meta">${bedrijf}</div>
  <div class="periode">Periode: ${datumBereik}</div>

  ${sectie("&#127777; Temperatuurmetingen", ["Datum", "Tijdstip", "Unit", "Type", "Temperatuur", "Norm", "Medewerker", "Opmerking", "Status"], tempRijen)}
  ${sectie("&#128230; Ontvangst", ["Datum", "Leverancier", "Product", "Temp", "THT OK", "Verpakking OK", "Medewerker", "Opmerking", "Status"], ontvangstRijen)}
  ${sectie("&#10052; Terugkoelen", ["Datum", "Product", "Starttijd", "Eindtijd", "Duur", "Eindtemp", "Medewerker", "Opmerking", "Status"], terugkoelRijen)}
  ${sectie("&#10024; Schoonmaak", ["Datum", "Taak", "Frequentie", "Medewerker"], schoonmaakRijen)}
  ${sectie("&#128680; Afwijkingen", ["Datum", "Bron", "Afwijking", "Maatregel / toelichting", "Medewerker"], afwijkingRijen)}
  ${sectie("&#129516; Hygiene / Ziekmeldingen", ["Datum", "Medewerker", "Klacht", "Thuis gebleven", "Opmerking"], hygieneRijen)}

  <div class="footer">
    <span>Gegenereerd op ${new Date().toLocaleString("nl-NL")}</span>
    <span>${bedrijf} — KitchenControl HACCP</span>
  </div>

  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`;

      const win = window.open("", "_blank", "width=900,height=700");
      if (win) { win.document.write(html); win.document.close(); }
      onClose();
    } catch (e) {
      setFout(`Fout bij ophalen data: ${e.message}`);
    } finally {
      setLaden(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.bg2, borderRadius: 12, width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", padding: "16px 20px", borderBottom: `1px solid ${C.bdr}` }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: C.txt, flex: 1 }}>📄 NVWA Rapport genereren</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, fontSize: 18, padding: 4 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ fontSize: 12, color: C.txt2, lineHeight: 1.6 }}>
            Genereer een overzicht van alle HACCP-registraties in de gekozen periode.
            Lege secties worden weggelaten. Het rapport opent als printbaar PDF-venster.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 5 }}>Van</label>
              <input type="date" value={van} onChange={e => setVan(e.target.value)}
                style={{ background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt, padding: "7px 10px", fontFamily: "inherit", fontSize: 13, borderRadius: 6, outline: "none", width: "100%" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 5 }}>Tot</label>
              <input type="date" value={tot} onChange={e => setTot(e.target.value)}
                style={{ background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt, padding: "7px 10px", fontFamily: "inherit", fontSize: 13, borderRadius: 6, outline: "none", width: "100%" }} />
            </div>
          </div>

          {fout && (
            <div style={{ padding: "8px 12px", background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 6, fontSize: 12, color: C.red }}>{fout}</div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={genereer} disabled={laden}
              style={{ flex: 1, background: laden ? C.bdr : C.primary, border: "none", color: laden ? C.txt3 : "#fff", padding: "10px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: laden ? "not-allowed" : "pointer", borderRadius: 6 }}>
              {laden ? "⏳ Laden…" : "📄 Rapport genereren"}
            </button>
            <button onClick={onClose}
              style={{ background: C.bg3, border: `1px solid ${C.bdr}`, color: C.txt2, padding: "10px 14px", fontFamily: "inherit", fontSize: 12, cursor: "pointer", borderRadius: 6 }}>
              Annuleren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
